import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Circle as CircleIcon,
  Devices as DevicesIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  Storage as StorageIcon,
  CalendarMonth as CalendarMonthIcon,
  EventRepeat as EventRepeatIcon,
  Email as EmailIcon,
  Public as PublicIcon,
  Store as StoreIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Archive as ArchiveIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
  ViewList as ViewListIcon,
  AccountTree as AccountTreeIcon,
  Warning as WarningIcon,
  LinkOff as LinkOffIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Storefront as StorefrontIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
  PersonOff as PersonOffIcon,
  Flag as FlagIcon,
  GppBad as GppBadIcon,
  CheckCircle as CheckCircleIcon,
  ArrowDownward as ArrowDownwardIcon,
  Extension as ExtensionIcon,
  Cancel as CancelIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const resolveApiBase = () => {
  const rawBase = process.env.REACT_APP_API_URL;
  if (!rawBase) {
    return '';
  }
  if (typeof window === 'undefined') {
    return rawBase;
  }
  try {
    return new URL(rawBase, window.location.origin).toString().replace(/\/$/, '');
  } catch {
    return '';
  }
};

const API_BASE = resolveApiBase();

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends
// the session cookie automatically on same-origin requests. localStorage
// never holds a token in the current scheme, so this is only a defensive
// fallback for legacy deployments; it must never throw or synthesize a
// bogus "Bearer null" header when the lookup comes back empty, since that
// would make the server reject requests even though the valid cookie is
// present and would otherwise authenticate them.
const getAuthTokenIfPresent = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

// Helper to get auth headers
const getAuthHeaders = (includeJsonContentType = false) => {
  const token = getAuthTokenIfPresent();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const portalFetch = (url, options = {}) => fetch(url, { credentials: 'include', ...options });

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

const readResponseBody = async (res) => {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json().catch(() => null);
  }
  return res.text().catch(() => '');
};

// Helper to handle API response with error checking
const handleResponse = async (res) => {
  const body = await readResponseBody(res);
  if (!res.ok) {
    const message = typeof body === 'string'
      ? body
      : body?.error || body?.message;
    const err = new Error(message || `Request failed with status ${res.status}`);
    if (typeof body === 'object' && body !== null) {
      err.reason = body.reason;
      err.warnings = body.warnings;
      err.currentTier = body.currentTier;
      err.totalRevenue = body.totalRevenue;
      err.paymentCount = body.paymentCount;
    }
    throw err;
  }
  return body ?? {};
};

// API functions
const companiesAPI = {
  getList: async (params) => {
    const queryString = buildQueryString(params);
    const res = await portalFetch(`${API_BASE}/api/companies?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getGrouped: async (params) => {
    // Get companies grouped by iCloud user
    const queryString = buildQueryString(params);
    const res = await portalFetch(`${API_BASE}/api/companies/grouped?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getOrphaned: async (params = {}) => {
    // Get companies without iCloud link
    const queryString = buildQueryString(params);
    const res = await portalFetch(`${API_BASE}/api/companies/orphaned?${queryString}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getStats: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getOnline: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/online`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getMonthlyStats: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/monthly-stats?months=12`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getActivityBreakdown: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/activity-breakdown`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getCumulativeGrowth: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/cumulative-growth?months=12`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getDetail: async (companyId) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getActiveTierUpgrade: async (companyId) => {
    const enc = encodeURIComponent(companyId);
    const res = await portalFetch(`${API_BASE}/api/tier-upgrades/company/${enc}/active`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  createTierUpgrade: async ({ companyId, upgradedTier, durationDays, reason }) => {
    const res = await portalFetch(`${API_BASE}/api/tier-upgrades`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ companyId, upgradedTier, durationDays, reason }),
    });
    return handleResponse(res);
  },
  cancelTierUpgrade: async ({ id, reason }) => {
    const res = await portalFetch(`${API_BASE}/api/tier-upgrades/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason: reason || '' }),
    });
    return handleResponse(res);
  },
  extendTierUpgrade: async ({ id, additionalDays }) => {
    const res = await portalFetch(`${API_BASE}/api/tier-upgrades/${encodeURIComponent(id)}/extend`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ additionalDays }),
    });
    return handleResponse(res);
  },
  getByIcloud: async (cloudKitRecordID) => {
    const encoded = encodeURIComponent(cloudKitRecordID);
    const res = await portalFetch(`${API_BASE}/api/companies/by-icloud/${encoded}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  changeTier: async ({ companyId, tier, reason }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/tier`, {
      method: 'POST',
	    headers: getAuthHeaders(true),
      body: JSON.stringify({ tier, reason }),
    });
    return handleResponse(res);
  },
  setClientHandoffCapability: async ({ companyId, enabled, reason, action }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/client-handoff-capability`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ enabled, reason, action }),
    });
    return handleResponse(res);
  },
  overrideMultiCompany: async ({ companyId, enabled, reason, action }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/override-multi-company`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ enabled, reason, action }),
    });
    return handleResponse(res);
  },
  overrideCompanyLimit: async ({ companyId, maxCompanies, reason, action }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/override-company-limit`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ maxCompanies, reason, action }),
    });
    return handleResponse(res);
  },
  overrideBusinessTypes: async ({ companyId, businessTypes, reason, action }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/override-business-types`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ businessTypes, reason, action }),
    });
    return handleResponse(res);
  },
  saveHostedAIAccountOverride: async (companyId, { hostedAIOverride, auditNote }) => {
    const body = { hostedAIOverride };
    if (auditNote != null && String(auditNote).trim() !== '') {
      body.auditNote = String(auditNote).trim();
    }
    const res = await portalFetch(
      `${API_BASE}/api/companies/${encodeURIComponent(companyId)}/hosted-ai-account-override`,
      {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(body),
      }
    );
    return handleResponse(res);
  },
  deleteCompany: async (companyId, { force = false } = {}) => {
    const url = force
      ? `${API_BASE}/api/companies/${companyId}?force=true`
      : `${API_BASE}/api/companies/${companyId}`;
    const res = await portalFetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
	exportCSV: async () => {
	  const res = await portalFetch(`${API_BASE}/api/companies/export/csv`, {
	    headers: {
	      ...getAuthHeaders(),
	      Accept: 'text/csv',
	    },
	  });

	  if (!res.ok) {
	    const contentType = res.headers.get('content-type') || '';
	    let message = `Failed to export CSV (status ${res.status})`;
	    if (contentType.includes('application/json')) {
	      const errorData = await res.json().catch(() => ({}));
	      message = errorData.error || errorData.message || message;
	    } else {
	      const text = await res.text().catch(() => '');
	      if (text) message = text;
	    }
	    throw new Error(message);
	  }

	  const blob = await res.blob();
	  const url = window.URL.createObjectURL(blob);
	  const a = document.createElement('a');
	  a.href = url;
	  a.download = `companies-${new Date().toISOString().slice(0, 10)}.csv`;
	  document.body.appendChild(a);
	  a.click();
	  a.remove();
	  window.URL.revokeObjectURL(url);
	},

  // ── Fraud management API ──────────────────────────────────────────────────
  getFraudStats: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/fraud-stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  getFraudQueue: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/fraud-queue`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  resolveFraud: async (companyId, { notes, resolvedBy }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/resolve-fraud`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ notes, resolvedBy }),
    });
    return handleResponse(res);
  },
  forceDowngrade: async (companyId, { reason }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/force-downgrade`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(res);
  },
  suspendAccount: async (companyId, { reason }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/suspend`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(res);
  },
  banUser: async (companyId, { reason }) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/ban-user`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(res);
  },
  unsuspendAccount: async (companyId, { notes } = {}) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/unsuspend`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ notes }),
    });
    return handleResponse(res);
  },
  unbanUser: async (companyId, { reason } = {}) => {
    const res = await portalFetch(`${API_BASE}/api/companies/${companyId}/unban-user`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(res);
  },
  exportFraudCSV: async () => {
    const res = await portalFetch(`${API_BASE}/api/companies/export/fraud-csv`, {
      headers: { ...getAuthHeaders(), Accept: 'text/csv' },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to export fraud report (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fraud-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Tier chip colors
const tierColors = {
  free: 'default',
  standard: 'warning',
  professional: 'primary',
  enterprise: 'secondary',
};

/** Same eligibility as Tier Upgrades page (Free / Professional only). */
const canGrantTemporaryTierUpgrade = (tier) => {
  const t = String(tier || '').toLowerCase();
  return t === 'free' || t === 'professional';
};

// Chart colors
const chartColors = {
  primary: 'rgb(25, 118, 210)',
  primaryLight: 'rgba(25, 118, 210, 0.2)',
  success: 'rgb(76, 175, 80)',
  successLight: 'rgba(76, 175, 80, 0.2)',
  warning: 'rgb(255, 152, 0)',
  warningLight: 'rgba(255, 152, 0, 0.2)',
  error: 'rgb(244, 67, 54)',
  errorLight: 'rgba(244, 67, 54, 0.2)',
  info: 'rgb(33, 150, 243)',
  infoLight: 'rgba(33, 150, 243, 0.2)',
  purple: 'rgb(156, 39, 176)',
  purpleLight: 'rgba(156, 39, 176, 0.2)',
  free: 'rgb(158, 158, 158)',
  professional: 'rgb(25, 118, 210)',
  enterprise: 'rgb(156, 39, 176)',
};

// Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Icon sx={{ fontSize: 48, color: `${color}.main`, opacity: 0.3 }} />
      </Box>
    </CardContent>
  </Card>
);

const HOSTED_AI_OVR_INHERIT = '';

function postgresOverrideToHostedAIForm(o) {
  const base = {
    hostedAIEnabled: HOSTED_AI_OVR_INHERIT,
    monthlyPromptLimitOverride: '',
    dailyPromptLimitOverride: '',
    bulkOperationsEnabledOverride: HOSTED_AI_OVR_INHERIT,
    maxItemsPerBulkJobOverride: '',
    maxBulkJobsPerDayOverride: '',
    requestsPerMinuteOverride: '',
    perUserRequestsPerMinuteOverride: '',
    maxConcurrentRequestsOverride: '',
    byokPrefer: HOSTED_AI_OVR_INHERIT,
    byokAutoFallback: HOSTED_AI_OVR_INHERIT,
    notes: '',
    operationCostOverridesText: '',
    auditNote: '',
  };
  if (!o || typeof o !== 'object') return base;
  return {
    ...base,
    hostedAIEnabled:
      o.hostedAIEnabled === true ? 'true' : o.hostedAIEnabled === false ? 'false' : HOSTED_AI_OVR_INHERIT,
    monthlyPromptLimitOverride:
      o.monthlyPromptLimitOverride != null && o.monthlyPromptLimitOverride !== ''
        ? String(o.monthlyPromptLimitOverride)
        : '',
    dailyPromptLimitOverride:
      o.dailyPromptLimitOverride != null && o.dailyPromptLimitOverride !== ''
        ? String(o.dailyPromptLimitOverride)
        : '',
    bulkOperationsEnabledOverride:
      o.bulkOperationsEnabledOverride === true
        ? 'true'
        : o.bulkOperationsEnabledOverride === false
          ? 'false'
          : HOSTED_AI_OVR_INHERIT,
    maxItemsPerBulkJobOverride:
      o.maxItemsPerBulkJobOverride != null ? String(o.maxItemsPerBulkJobOverride) : '',
    maxBulkJobsPerDayOverride:
      o.maxBulkJobsPerDayOverride != null ? String(o.maxBulkJobsPerDayOverride) : '',
    requestsPerMinuteOverride:
      o.requestsPerMinuteOverride != null ? String(o.requestsPerMinuteOverride) : '',
    perUserRequestsPerMinuteOverride:
      o.perUserRequestsPerMinuteOverride != null ? String(o.perUserRequestsPerMinuteOverride) : '',
    maxConcurrentRequestsOverride:
      o.maxConcurrentRequestsOverride != null ? String(o.maxConcurrentRequestsOverride) : '',
    byokPrefer:
      o.byokFallbackOverride?.preferBYOKWhenConfigured === true
        ? 'true'
        : o.byokFallbackOverride?.preferBYOKWhenConfigured === false
          ? 'false'
          : HOSTED_AI_OVR_INHERIT,
    byokAutoFallback:
      o.byokFallbackOverride?.autoFallbackOnHostedExhaustion === true
        ? 'true'
        : o.byokFallbackOverride?.autoFallbackOnHostedExhaustion === false
          ? 'false'
          : HOSTED_AI_OVR_INHERIT,
    notes: o.notes != null ? String(o.notes) : '',
    operationCostOverridesText: o.operationCostOverrides
      ? JSON.stringify(o.operationCostOverrides, null, 2)
      : '',
  };
}

/** Keys edited by the BI form — stripped from previous JSON before merging so inherit clears a field. */
const HOSTED_AI_OVERRIDE_MANAGED_KEYS = new Set([
  'hostedAIEnabled',
  'monthlyPromptLimitOverride',
  'dailyPromptLimitOverride',
  'bulkOperationsEnabledOverride',
  'maxItemsPerBulkJobOverride',
  'maxBulkJobsPerDayOverride',
  'requestsPerMinuteOverride',
  'perUserRequestsPerMinuteOverride',
  'maxConcurrentRequestsOverride',
  'byokFallbackOverride',
  'notes',
  'operationCostOverrides',
]);

function stripManagedHostedAIKeys(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = { ...raw };
  HOSTED_AI_OVERRIDE_MANAGED_KEYS.forEach((k) => {
    delete out[k];
  });
  return out;
}

/** @returns {{ hostedAIOverride: Record<string, unknown>, auditNote?: string }} */
function buildHostedAIOverridePayload(form) {
  const auditNote = form.auditNote?.trim() || undefined;
  const opText = form.operationCostOverridesText?.trim() || '';
  let operationCostOverrides;
  if (opText) {
    operationCostOverrides = JSON.parse(opText);
    if (
      !operationCostOverrides ||
      typeof operationCostOverrides !== 'object' ||
      Array.isArray(operationCostOverrides)
    ) {
      throw new Error('Operation cost overrides must be a JSON object');
    }
  }

  const hostedAIOverride = {};
  if (form.hostedAIEnabled === 'true') hostedAIOverride.hostedAIEnabled = true;
  if (form.hostedAIEnabled === 'false') hostedAIOverride.hostedAIEnabled = false;

  const numField = (key, raw) => {
    const t = raw?.trim?.();
    if (t === '' || t == null) return;
    const n = Number(t);
    if (!Number.isFinite(n)) throw new Error('Invalid number in override form');
    hostedAIOverride[key] = n;
  };
  numField('monthlyPromptLimitOverride', form.monthlyPromptLimitOverride);
  numField('dailyPromptLimitOverride', form.dailyPromptLimitOverride);
  numField('maxItemsPerBulkJobOverride', form.maxItemsPerBulkJobOverride);
  numField('maxBulkJobsPerDayOverride', form.maxBulkJobsPerDayOverride);
  numField('requestsPerMinuteOverride', form.requestsPerMinuteOverride);
  numField('perUserRequestsPerMinuteOverride', form.perUserRequestsPerMinuteOverride);
  numField('maxConcurrentRequestsOverride', form.maxConcurrentRequestsOverride);

  if (form.bulkOperationsEnabledOverride === 'true') hostedAIOverride.bulkOperationsEnabledOverride = true;
  if (form.bulkOperationsEnabledOverride === 'false') hostedAIOverride.bulkOperationsEnabledOverride = false;

  const byokFb = {};
  if (form.byokPrefer === 'true') byokFb.preferBYOKWhenConfigured = true;
  if (form.byokPrefer === 'false') byokFb.preferBYOKWhenConfigured = false;
  if (form.byokAutoFallback === 'true') byokFb.autoFallbackOnHostedExhaustion = true;
  if (form.byokAutoFallback === 'false') byokFb.autoFallbackOnHostedExhaustion = false;
  if (Object.keys(byokFb).length > 0) hostedAIOverride.byokFallbackOverride = byokFb;

  if (form.notes?.trim()) hostedAIOverride.notes = form.notes.trim();
  if (operationCostOverrides) hostedAIOverride.operationCostOverrides = operationCostOverrides;

  return {
    hostedAIOverride,
    auditNote,
  };
}

// Catalog of business types that can be granted as a per-account override.
// Must stay in sync with business-intelligence-portal/constants/businessTypes.js
// and the iOS BusinessType enum in Novo Solution/Models/Company.swift.
const BUSINESS_TYPE_CATALOG = [
  { key: 'jewelry', label: 'Jewelry Store' },
  { key: 'watches', label: 'Watch Store' },
  { key: 'electronics', label: 'Electronics' },
  { key: 'grocery', label: 'Grocery' },
  { key: 'automotive', label: 'Car Dealership' },
  { key: 'clothing', label: 'Clothing' },
  { key: 'restaurant', label: 'Restaurant' },
  { key: 'coffee_shop', label: 'Coffee Shop' },
  { key: 'wine_cellar', label: 'Wine & Cellar' },
  { key: 'hardware', label: 'Hardware' },
  { key: 'bookstore', label: 'Bookstore' },
  { key: 'hair_beauty_salon', label: 'Hair & Beauty Salon' },
  { key: 'aesthetics_clinic', label: 'Aesthetics Clinic' },
  { key: 'gemstone', label: 'Gemstone Wholesaler' },
  { key: 'real_estate', label: 'Real Estate' },
  { key: 'general', label: 'General' },
];

const Companies = () => {
  const queryClient = useQueryClient();
  const refreshAfterFraudAction = useCallback(() => {
    queryClient.invalidateQueries('company-detail');
    queryClient.invalidateQueries('companies');
    queryClient.invalidateQueries('companies-grouped');
    queryClient.invalidateQueries('fraud-queue');
    queryClient.invalidateQueries('fraud-stats');
    queryClient.invalidateQueries('company-stats');
  }, [queryClient]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('registrationDate');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'archived', or 'all'
  const [exportError, setExportError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleteForceAcknowledged, setDeleteForceAcknowledged] = useState(false);
  const [deletePayingError, setDeletePayingError] = useState(null);

  const [tierUpgradeGrantOpen, setTierUpgradeGrantOpen] = useState(false);
  const [tierUpgradeCancelOpen, setTierUpgradeCancelOpen] = useState(false);
  const [tierUpgradeExtendOpen, setTierUpgradeExtendOpen] = useState(false);
  const [tierUpgradeForm, setTierUpgradeForm] = useState({
    upgradedTier: 'professional',
    durationDays: 30,
    reason: '',
  });
  const [tierUpgradeCancelReason, setTierUpgradeCancelReason] = useState('');
  const [tierUpgradeExtendDays, setTierUpgradeExtendDays] = useState(7);
  const [tierUpgradeError, setTierUpgradeError] = useState(null);

  // View mode: 'list' or 'grouped' (by iCloud user)
  const [viewMode, setViewMode] = useState('list');
  const isGroupedView = viewMode === 'grouped';

  // Grouped view state
  const [groupedPage, setGroupedPage] = useState(0);
  const [onlyMultiCompany, setOnlyMultiCompany] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [includeArchivedInGrouped, setIncludeArchivedInGrouped] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  // Expandable store rows within the grouped view (BY ICLOUD tab)
  const [groupedViewExpandedStoreRows, setGroupedViewExpandedStoreRows] = useState({});

  // Grouping view: companies belonging to the same iCloud user (cloudKitRecordID)
  const [icloudDialogOpen, setIcloudDialogOpen] = useState(false);
  const [selectedCloudKitRecordID, setSelectedCloudKitRecordID] = useState(null);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [selectedStoreCompanyId, setSelectedStoreCompanyId] = useState(null);
  // Expandable store rows within the iCloud dialog
  const [icloudDialogExpandedRows, setIcloudDialogExpandedRows] = useState({});

  // Expandable store rows: tracks which company rows have their store sub-section expanded
  const [expandedStoreRows, setExpandedStoreRows] = useState({});
  // Cache for fetched store data per company (to avoid re-fetching on collapse/expand)
  const [storeRowData, setStoreRowData] = useState({});

  // Company limit override modal state
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideMaxCompanies, setOverrideMaxCompanies] = useState('');
  const [overrideUnlimited, setOverrideUnlimited] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [overrideSuccess, setOverrideSuccess] = useState(null);

  // Per-account business-type override modal state
  const [btOverrideModalOpen, setBtOverrideModalOpen] = useState(false);
  const [btOverrideSelected, setBtOverrideSelected] = useState([]);
  const [btOverrideReason, setBtOverrideReason] = useState('');
  const [btOverrideLoading, setBtOverrideLoading] = useState(false);
  const [btOverrideError, setBtOverrideError] = useState(null);
  const [btOverrideSuccess, setBtOverrideSuccess] = useState(null);

  // Permanent tier change (company detail)
  const [changeTierOpen, setChangeTierOpen] = useState(false);
  const [changeTierValue, setChangeTierValue] = useState('free');
  const [changeTierReason, setChangeTierReason] = useState('');
  const [changeTierError, setChangeTierError] = useState(null);

  // Client handoff capability
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffError, setHandoffError] = useState(null);
  const [handoffSuccess, setHandoffSuccess] = useState(null);
  const [handoffReason, setHandoffReason] = useState('');

  // Multi-company global kill-switch override
  const [multiCompanyOverrideLoading, setMultiCompanyOverrideLoading] = useState(false);
  const [multiCompanyOverrideError, setMultiCompanyOverrideError] = useState(null);
  const [multiCompanyOverrideSuccess, setMultiCompanyOverrideSuccess] = useState(null);
  const [multiCompanyOverrideReason, setMultiCompanyOverrideReason] = useState('');

  // ── Fraud management state ────────────────────────────────────────────────
  // selectedCompanyDetail holds the company targeted by a fraud action opened
  // from the Fraud Review Queue (tab 3). Falls back to detailData?.company when
  // the action is opened from within the company detail dialog.
  const [selectedCompanyDetail, setSelectedCompanyDetail] = useState(null);
  const [resolveFraudOpen, setResolveFraudOpen] = useState(false);
  const [resolveFraudNotes, setResolveFraudNotes] = useState('');
  const [forceDowngradeOpen, setForceDowngradeOpen] = useState(false);
  const [forceDowngradeReason, setForceDowngradeReason] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [banOpen, setBanOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [unsuspendOpen, setUnsuspendOpen] = useState(false);
  const [unsuspendNotes, setUnsuspendNotes] = useState('');
  const [unbanOpen, setUnbanOpen] = useState(false);
  const [unbanReason, setUnbanReason] = useState('');
  const [fraudActionLoading, setFraudActionLoading] = useState(false);
  const [fraudActionError, setFraudActionError] = useState(null);
  const [fraudActionSuccess, setFraudActionSuccess] = useState(null);

  const [hostedAIOvrForm, setHostedAIOvrForm] = useState(() => postgresOverrideToHostedAIForm(null));
  const [hostedAIOvrBusy, setHostedAIOvrBusy] = useState(false);
  const [hostedAIOvrMsg, setHostedAIOvrMsg] = useState(null);

  const handleExportCSV = useCallback(async () => {
    try {
      setExportError(null);
      await companiesAPI.exportCSV();
    } catch (e) {
      setExportError(e?.message || 'Failed to export CSV');
    }
  }, []);

  // Fetch companies list
  const { data: companiesData, isLoading: listLoading, error: listError, refetch } = useQuery(
    ['companies', page, rowsPerPage, orderBy, order, search, tierFilter, statusFilter],
    () => companiesAPI.getList({
      page: page + 1,
      limit: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order,
      search: search || undefined,
      tier: tierFilter || undefined,
      status: statusFilter || undefined,
    }),
    { keepPreviousData: true, enabled: viewMode === 'list', refetchOnWindowFocus: false }
  );

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery(
    'company-stats',
    companiesAPI.getStats,
    {
      refetchInterval: isGroupedView ? false : 90000,
      enabled: !isGroupedView,
      refetchOnWindowFocus: false
    }
  );

  // Fetch online companies
  const { data: onlineData } = useQuery(
    'companies-online',
    companiesAPI.getOnline,
    {
      refetchInterval: isGroupedView ? false : 10000,
      enabled: !isGroupedView,
      refetchOnWindowFocus: false
    }
  );

  // Fetch monthly stats for charts
  const { data: monthlyData } = useQuery(
    'company-monthly-stats',
    companiesAPI.getMonthlyStats,
    {
      refetchInterval: isGroupedView ? false : 60000,
      enabled: !isGroupedView,
      refetchOnWindowFocus: false
    }
  );

  // Fetch activity breakdown
  const { data: activityData } = useQuery(
    'company-activity-breakdown',
    companiesAPI.getActivityBreakdown,
    {
      refetchInterval: isGroupedView ? false : 60000,
      enabled: !isGroupedView,
      refetchOnWindowFocus: false
    }
  );

  // Fetch cumulative growth
  const { data: growthData } = useQuery(
    'company-cumulative-growth',
    companiesAPI.getCumulativeGrowth,
    {
      refetchInterval: isGroupedView ? false : 60000,
      enabled: !isGroupedView,
      refetchOnWindowFocus: false
    }
  );

  // Fetch company detail
  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['company-detail', selectedCompany],
    () => companiesAPI.getDetail(selectedCompany),
    { enabled: !!selectedCompany }
  );

  useEffect(() => {
    if (!detailOpen || !detailData) return;
    setHostedAIOvrForm(postgresOverrideToHostedAIForm(detailData.postgresHostedAIOverride));
    setHostedAIOvrMsg(null);
  }, [detailOpen, detailData, selectedCompany]);

  const { data: activeTierUpgradeData, isLoading: activeTierUpgradeLoading } = useQuery(
    ['tier-upgrade-active', selectedCompany],
    () => companiesAPI.getActiveTierUpgrade(selectedCompany),
    { enabled: detailOpen && !!selectedCompany, staleTime: 15_000 }
  );

  // Fetch all companies for an iCloud user (grouped view)
  const { data: icloudCompaniesData, isLoading: icloudCompaniesLoading, error: icloudCompaniesError } = useQuery(
    ['icloud-companies', selectedCloudKitRecordID],
    () => companiesAPI.getByIcloud(selectedCloudKitRecordID),
    { enabled: icloudDialogOpen && !!selectedCloudKitRecordID }
  );

  // Fetch company store details for store/branch dialog
  const { data: storeDialogData, isLoading: storeDialogLoading, error: storeDialogError } = useQuery(
    ['company-store-detail', selectedStoreCompanyId],
    () => companiesAPI.getDetail(selectedStoreCompanyId),
    { enabled: storeDialogOpen && !!selectedStoreCompanyId }
  );

  // Fetch grouped companies (by iCloud user)
  // eslint-disable-next-line no-unused-vars
  const { data: groupedData, isLoading: groupedLoading, error: groupedError, refetch: refetchGrouped } = useQuery(
    ['companies-grouped', groupedPage, rowsPerPage, search, tierFilter, onlyMultiCompany, includeArchivedInGrouped],
    () => companiesAPI.getGrouped({
      page: groupedPage + 1,
      limit: rowsPerPage,
      search: search || undefined,
      tier: tierFilter || undefined,
      onlyMultiCompany: onlyMultiCompany ? 'true' : 'false',
      includeArchived: includeArchivedInGrouped ? 'true' : 'false',
    }),
    { enabled: viewMode === 'grouped', keepPreviousData: true }
  );

  // Fetch orphaned companies
  // eslint-disable-next-line no-unused-vars
  const { data: orphanedData, isLoading: orphanedLoading } = useQuery(
    'companies-orphaned',
    () => companiesAPI.getOrphaned({ limit: 10 }),
    { enabled: viewMode === 'grouped' }
  );

  // Fetch fraud stats (only when Fraud Management tab is active)
  const { data: fraudStatsData, isLoading: fraudStatsLoading, refetch: refetchFraudStats } = useQuery(
    'fraud-stats',
    companiesAPI.getFraudStats,
    { enabled: activeTab === 3, refetchOnWindowFocus: false }
  );

  // Fetch fraud review queue (only when Fraud Management tab is active)
  const { data: fraudQueueData, isLoading: fraudQueueLoading, refetch: refetchFraudQueue } = useQuery(
    'fraud-queue',
    companiesAPI.getFraudQueue,
    { enabled: activeTab === 3, refetchOnWindowFocus: false }
  );

  // Change tier mutation (used in detail dialog for tier changes)
  const changeTierMutation = useMutation(companiesAPI.changeTier, {
    onSuccess: () => {
      queryClient.invalidateQueries('companies');
      queryClient.invalidateQueries('company-stats');
      queryClient.invalidateQueries(['company-detail', selectedCompany]);
      setChangeTierOpen(false);
      setChangeTierReason('');
      setChangeTierError(null);
    },
    onError: (e) => {
      setChangeTierError(e?.message || 'Failed to change company tier');
    },
  });

  const createTierUpgradeMutation = useMutation(
    (payload) => companiesAPI.createTierUpgrade(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tier-upgrade-active', selectedCompany]);
        queryClient.invalidateQueries(['company-detail', selectedCompany]);
        queryClient.invalidateQueries('tierUpgrades');
        queryClient.invalidateQueries('tierUpgradeStats');
        queryClient.invalidateQueries('companies');
        setTierUpgradeGrantOpen(false);
        setTierUpgradeForm({ upgradedTier: 'professional', durationDays: 30, reason: '' });
        setTierUpgradeError(null);
      },
      onError: (e) => {
        setTierUpgradeError(e?.message || 'Failed to grant temporary tier upgrade');
      },
    }
  );

  const cancelTierUpgradeMutation = useMutation(
    (payload) => companiesAPI.cancelTierUpgrade(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tier-upgrade-active', selectedCompany]);
        queryClient.invalidateQueries(['company-detail', selectedCompany]);
        queryClient.invalidateQueries('tierUpgrades');
        queryClient.invalidateQueries('tierUpgradeStats');
        queryClient.invalidateQueries('companies');
        setTierUpgradeCancelOpen(false);
        setTierUpgradeCancelReason('');
      },
      onError: (e) => {
        setTierUpgradeError(e?.message || 'Failed to cancel tier upgrade');
      },
    }
  );

  const extendTierUpgradeMutation = useMutation(
    (payload) => companiesAPI.extendTierUpgrade(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tier-upgrade-active', selectedCompany]);
        queryClient.invalidateQueries(['company-detail', selectedCompany]);
        queryClient.invalidateQueries('tierUpgrades');
        queryClient.invalidateQueries('tierUpgradeStats');
        setTierUpgradeExtendOpen(false);
      },
      onError: (e) => {
        setTierUpgradeError(e?.message || 'Failed to extend tier upgrade');
      },
    }
  );

  const deleteCompanyMutation = useMutation(
    ({ companyId, force }) => companiesAPI.deleteCompany(companyId, { force }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        queryClient.invalidateQueries('company-stats');
        queryClient.invalidateQueries('company-monthly-stats');
        queryClient.invalidateQueries('company-activity-breakdown');
        queryClient.invalidateQueries('company-cumulative-growth');
        setDetailOpen(false);
        setSelectedCompany(null);
        setDeleteConfirmOpen(false);
        setDeleteConfirmName('');
        setCompanyToDelete(null);
        setDeleteForceAcknowledged(false);
        setDeletePayingError(null);
      },
      onError: (error) => {
        if (error?.reason === 'PAYING_CUSTOMER') {
          setDeletePayingError(error);
        }
      },
    }
  );

  const handleDeleteCompany = useCallback((companyId, companyName, tier, totalRevenue, paymentCount, subscriptionStatus) => {
    setCompanyToDelete({ companyId, companyName, tier, totalRevenue, paymentCount, subscriptionStatus });
    setDeleteConfirmName('');
    setDeleteForceAcknowledged(false);
    setDeletePayingError(null);
    setDeleteConfirmOpen(true);
  }, []);

  const confirmDeleteCompany = useCallback(() => {
    if (!companyToDelete || deleteConfirmName !== companyToDelete.companyName) return;
    const isPaid = ['standard', 'professional', 'enterprise'].includes(companyToDelete.tier);
    const hasPay = (companyToDelete.paymentCount || 0) > 0 || (companyToDelete.totalRevenue || 0) > 0;
    const needsForce = isPaid || hasPay;
    if (needsForce && !deleteForceAcknowledged) {
      setDeletePayingError({
        warnings: [
          isPaid ? `Active subscription: ${companyToDelete.tier?.toUpperCase()} tier` : null,
          hasPay ? `Revenue/payments on record` : null,
        ].filter(Boolean),
      });
      return;
    }
    deleteCompanyMutation.mutate({
      companyId: companyToDelete.companyId,
      force: needsForce && deleteForceAcknowledged,
    });
  }, [companyToDelete, deleteConfirmName, deleteForceAcknowledged, deleteCompanyMutation]);

  // Company limit override handlers
  const handleGrantOverride = useCallback(async () => {
    const companyId = detailData?.company?.companyId;
    if (!companyId) return;
    setOverrideLoading(true);
    setOverrideError(null);
    setOverrideSuccess(null);
    try {
      const maxCompaniesValue = overrideUnlimited ? -1 : parseInt(overrideMaxCompanies, 10);
      const result = await companiesAPI.overrideCompanyLimit({
        companyId,
        maxCompanies: maxCompaniesValue,
        reason: overrideReason,
        action: 'grant',
      });
      setOverrideSuccess(result.message + (result.siblingsPropagated > 0 ? ` (propagated to ${result.siblingsPropagated} sibling companies)` : ''));
      setOverrideModalOpen(false);
      setOverrideMaxCompanies('');
      setOverrideUnlimited(false);
      setOverrideReason('');
      queryClient.invalidateQueries(['company-detail', selectedCompany]);
    } catch (err) {
      setOverrideError(err.message || 'Failed to grant override');
    } finally {
      setOverrideLoading(false);
    }
  }, [detailData, overrideMaxCompanies, overrideUnlimited, overrideReason, selectedCompany, queryClient]);

  const handleRemoveOverride = useCallback(async () => {
    const companyId = detailData?.company?.companyId;
    if (!companyId) return;
    const confirmed = window.confirm(
      'Remove the company-limit override for this entire iCloud account?\n\n' +
      'This clears the override on all companies under the same iCloud ID. ' +
      'Your other companies on this account will return to their tier default limit.'
    );
    if (!confirmed) return;
    setOverrideLoading(true);
    setOverrideError(null);
    setOverrideSuccess(null);
    try {
      const result = await companiesAPI.overrideCompanyLimit({
        companyId,
        action: 'remove',
        reason: 'Override removed by admin',
      });
      setOverrideSuccess(
        result.message +
        (result.siblingsPropagated > 0 ? ` (cleared ${result.siblingsPropagated} sibling companies)` : '')
      );
      queryClient.invalidateQueries(['company-detail', selectedCompany]);
      queryClient.invalidateQueries('companies');
    } catch (err) {
      setOverrideError(err.message || 'Failed to remove override');
    } finally {
      setOverrideLoading(false);
    }
  }, [detailData, selectedCompany, queryClient]);

  // Per-account business-type override handlers
  const handleGrantBusinessTypesOverride = useCallback(async () => {
    const companyId = detailData?.company?.companyId;
    if (!companyId) return;
    setBtOverrideLoading(true);
    setBtOverrideError(null);
    setBtOverrideSuccess(null);
    try {
      const result = await companiesAPI.overrideBusinessTypes({
        companyId,
        businessTypes: btOverrideSelected,
        reason: btOverrideReason,
        action: 'grant',
      });
      setBtOverrideSuccess(
        result.message + (result.siblingsPropagated > 0
          ? ` (propagated to ${result.siblingsPropagated} sibling companies)`
          : '')
      );
      setBtOverrideModalOpen(false);
      setBtOverrideSelected([]);
      setBtOverrideReason('');
      queryClient.invalidateQueries(['company-detail', selectedCompany]);
    } catch (err) {
      setBtOverrideError(err.message || 'Failed to grant business-type override');
    } finally {
      setBtOverrideLoading(false);
    }
  }, [detailData, btOverrideSelected, btOverrideReason, selectedCompany, queryClient]);

  const handleRemoveBusinessTypesOverride = useCallback(async () => {
    const companyId = detailData?.company?.companyId;
    if (!companyId) return;
    setBtOverrideLoading(true);
    setBtOverrideError(null);
    setBtOverrideSuccess(null);
    try {
      const result = await companiesAPI.overrideBusinessTypes({
        companyId,
        action: 'remove',
        reason: 'Override removed by admin',
      });
      setBtOverrideSuccess(result.message);
      queryClient.invalidateQueries(['company-detail', selectedCompany]);
    } catch (err) {
      setBtOverrideError(err.message || 'Failed to remove business-type override');
    } finally {
      setBtOverrideLoading(false);
    }
  }, [detailData, selectedCompany, queryClient]);

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, order]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(0);
  }, []);

  const handleRowClick = useCallback((companyId) => {
    setSelectedCompany(companyId);
    setDetailOpen(true);
  }, []);

  const openIcloudCompaniesDialog = useCallback((cloudKitRecordID) => {
    if (!cloudKitRecordID) return;
    setSelectedCloudKitRecordID(cloudKitRecordID);
    setIcloudDialogOpen(true);
  }, []);

  const closeIcloudCompaniesDialog = useCallback(() => {
    setIcloudDialogOpen(false);
    setIcloudDialogExpandedRows({}); // Reset expanded rows when dialog closes
  }, []);

  // Toggle store expansion for a company in the iCloud dialog
  const toggleIcloudDialogStoreExpansion = useCallback((companyId) => {
    setIcloudDialogExpandedRows(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  }, []);

  // Toggle store expansion for a company in the grouped view (BY ICLOUD tab)
  const toggleGroupedViewStoreExpansion = useCallback((companyId) => {
    setGroupedViewExpandedStoreRows(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  }, []);

  const openStoreDialog = useCallback((companyId) => {
    if (!companyId) return;
    setSelectedStoreCompanyId(companyId);
    setStoreDialogOpen(true);
  }, []);

  const closeStoreDialog = useCallback(() => {
    setStoreDialogOpen(false);
  }, []);

  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setPage(0);
      setGroupedPage(0);
    }
  }, []);

  const toggleGroupExpanded = useCallback((groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);

  const getStoreMeta = useCallback((company) => {
    const derivedCount = company.storeCount
      ?? company.storeStats?.totalStores
      ?? company.stores?.length;
    const storeCount = Number.isFinite(derivedCount) ? derivedCount : 1;
    const isMultiStore = storeCount > 1 || company.storeStats?.isMultiStore || company.isMultiStore;
    return { storeCount, isMultiStore };
  }, []);

  // Toggle store row expansion for multi-store companies
  const toggleStoreRowExpansion = useCallback(async (companyId, isMultiStore) => {
    if (!isMultiStore) return; // Only multi-store companies can expand

    const isCurrentlyExpanded = expandedStoreRows[companyId];

    // Toggle the expansion state
    setExpandedStoreRows(prev => ({
      ...prev,
      [companyId]: !isCurrentlyExpanded
    }));

    // If expanding and we don't have data yet, fetch it
    if (!isCurrentlyExpanded && !storeRowData[companyId]) {
      try {
        const detail = await companiesAPI.getDetail(companyId);
        setStoreRowData(prev => ({
          ...prev,
          [companyId]: {
            stores: detail.storeStats?.stores || [],
            teamMembersPerStore: detail.teamMembers?.perStoreBreakdown || detail.company?.usageMetrics?.teamMembersPerStore || [],
            totalStores: detail.storeStats?.totalStores || 0,
            loading: false,
            error: null
          }
        }));
      } catch (error) {
        console.error('Failed to fetch store details:', error);
        setStoreRowData(prev => ({
          ...prev,
          [companyId]: {
            stores: [],
            teamMembersPerStore: [],
            totalStores: 0,
            loading: false,
            error: error.message
          }
        }));
      }
    }
  }, [expandedStoreRows, storeRowData]);

  const stats = statsData?.stats || {};
  const companies = companiesData?.companies || [];
  const pagination = companiesData?.pagination || { total: 0 };
  const storeDialogStats = storeDialogData?.storeStats;
  const storeDialogStores = storeDialogStats?.stores || [];
  const storeDialogHeadquarters = storeDialogStores.filter(store => store.isHeadquarters);
  const storeDialogBranches = storeDialogStores.filter(store => !store.isHeadquarters);

  if (listLoading && !companiesData) {
    return <LoadingSpinner />;
  }

  // Prepare chart data
  const monthlyStats = monthlyData?.stats || [];
  const activity = activityData?.breakdown || {};
  const growth = growthData?.growth || [];

  // Monthly new companies chart data
  const newCompaniesChartData = {
    labels: monthlyStats.map(m => m.label),
    datasets: [
      {
        label: 'New Companies',
        data: monthlyStats.map(m => m.newCompanies),
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primaryLight,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Tier distribution by month (stacked bar)
  const tierByMonthChartData = {
    labels: monthlyStats.map(m => m.label),
    datasets: [
      {
        label: 'Free',
        data: monthlyStats.map(m => m.byTier?.free || 0),
        backgroundColor: chartColors.free,
      },
      {
        label: 'Professional',
        data: monthlyStats.map(m => m.byTier?.professional || 0),
        backgroundColor: chartColors.professional,
      },
      {
        label: 'Enterprise',
        data: monthlyStats.map(m => m.byTier?.enterprise || 0),
        backgroundColor: chartColors.enterprise,
      },
    ],
  };

  // Cumulative growth chart
  const cumulativeChartData = {
    labels: growth.map(g => g.label),
    datasets: [
      {
        label: 'Total Companies',
        data: growth.map(g => g.totalCompanies),
        borderColor: chartColors.success,
        backgroundColor: chartColors.successLight,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Activity breakdown doughnut
  const activityChartData = {
    labels: ['Online Now', 'Active Today', 'Active This Week', 'Active This Month', 'Dormant'],
    datasets: [
      {
        data: [
          activity.onlineNow || 0,
          activity.activeToday || 0,
          activity.activeThisWeek || 0,
          activity.activeThisMonth || 0,
          activity.dormant || 0,
        ],
        backgroundColor: [
          chartColors.success,
          chartColors.info,
          chartColors.primary,
          chartColors.warning,
          chartColors.error,
        ],
      },
    ],
  };

  // Tier distribution doughnut
  const tierDistributionData = {
    labels: ['Free', 'Professional', 'Enterprise'],
    datasets: [
      {
        data: [
          stats.byTier?.free || 0,
          stats.byTier?.professional || 0,
          stats.byTier?.enterprise || 0,
        ],
        backgroundColor: [chartColors.free, chartColors.professional, chartColors.enterprise],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {(listError || exportError) && (
        <Box sx={{ mb: 2 }}>
          {listError && (
            <Alert severity="error" sx={{ mb: exportError ? 1 : 0 }}>
              Failed to load companies: {listError.message}
            </Alert>
          )}
          {exportError && (
            <Alert severity="error">
              CSV export failed: {exportError}
            </Alert>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { refetch(); queryClient.invalidateQueries(); }} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatsCard
            title="Total Companies"
            value={statsLoading ? '...' : stats.totalCompanies || 0}
            subtitle="Unique accounts"
            icon={BusinessIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatsCard
            title="Active (30 days)"
            value={statsLoading ? '...' : stats.activeCompanies || 0}
            subtitle={`${activity.dormant || 0} dormant`}
            icon={TrendingUpIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatsCard
            title="Online Now"
            value={onlineData?.count || 0}
            subtitle={`${onlineData?.totalTeamMembers || 0} team members across ${onlineData?.totalDevices || 0} devices`}
            icon={PeopleIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatsCard
            title="Total Devices"
            value={statsLoading ? '...' : stats.totalDevices || 0}
            subtitle={`${statsLoading ? '...' : stats.totalTeamMembers || 0} team members total`}
            icon={DevicesIcon}
            color="secondary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatsCard
            title="New This Week"
            value={statsLoading ? '...' : stats.recentRegistrations || 0}
            subtitle="Recent signups"
            icon={TrendingUpIcon}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Tabs for Analytics and Companies List */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Analytics" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Companies List" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Real-Time" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Fraud Management" icon={<SecurityIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Analytics Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Charts Row 1: Growth Overview */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Company Growth (Cumulative)</Typography>
                  <Box sx={{ height: 300 }}>
                    <Line data={cumulativeChartData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Tier Distribution</Typography>
                  <Box sx={{ height: 300 }}>
                    <Doughnut data={tierDistributionData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 2: Monthly Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>New Companies Per Month</Typography>
                  <Box sx={{ height: 300 }}>
                    <Line data={newCompaniesChartData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>New Companies by Tier</Typography>
                  <Box sx={{ height: 300 }}>
                    <Bar data={tierByMonthChartData} options={barChartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 3: Activity */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Activity Breakdown</Typography>
                  <Box sx={{ height: 300 }}>
                    <Doughnut data={activityChartData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Activity Summary</Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.onlineNow || 0}</Typography>
                        <Typography variant="body2">Online Now</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.activeToday || 0}</Typography>
                        <Typography variant="body2">Active Today</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.activeThisWeek || 0}</Typography>
                        <Typography variant="body2">Active This Week</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.activeThisMonth || 0}</Typography>
                        <Typography variant="body2">Active This Month</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.dormant || 0}</Typography>
                        <Typography variant="body2">Dormant (90+ days)</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.200', borderRadius: 2 }}>
                        <Typography variant="h4">{activity.total || 0}</Typography>
                        <Typography variant="body2">Total Companies</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Companies List Tab */}
      {activeTab === 1 && (
        <Box>
          {/* View Mode Toggle and Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                {/* View Mode Toggle */}
                <Grid item xs={12} md={3}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="list" aria-label="list view">
                      <Tooltip title="List View">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ViewListIcon />
                          <Typography variant="caption">List</Typography>
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="grouped" aria-label="grouped by icloud">
                      <Tooltip title="Grouped by iCloud User">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccountTreeIcon />
                          <Typography variant="caption">By iCloud</Typography>
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {/* Search */}
                <Grid item xs={12} md={viewMode === 'grouped' ? 5 : 5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, ID, email, or iCloud ID..."
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Tier Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tier</InputLabel>
                    <Select
                      value={tierFilter}
                      label="Tier"
                      onChange={(e) => { setTierFilter(e.target.value); setPage(0); setGroupedPage(0); }}
                    >
                      <MenuItem value="">All Tiers</MenuItem>
                      <MenuItem value="free">Free</MenuItem>
                      <MenuItem value="professional">Professional</MenuItem>
                      <MenuItem value="enterprise">Enterprise</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Filter - only for list view */}
                {viewMode === 'list' && (
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Grouped View Options */}
                {viewMode === 'grouped' && (
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={onlyMultiCompany}
                          onChange={(e) => { setOnlyMultiCompany(e.target.checked); setGroupedPage(0); }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">Multi-company only</Typography>
                      }
                    />
                  </Grid>
                )}
              </Grid>

              {/* Grouped View Summary Stats */}
              {viewMode === 'grouped' && groupedData?.summary && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<CloudIcon />}
                    label={`${groupedData.summary.totalICloudAccounts} iCloud Accounts`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<BusinessIcon />}
                    label={`${groupedData.summary.totalMultiCompanyAccounts} Multi-Company`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  {groupedData.summary.totalOrphanedCompanies > 0 && (
                    <Tooltip title="Companies without iCloud link - may need migration">
                      <Chip
                        icon={<WarningIcon />}
                        label={`${groupedData.summary.totalOrphanedCompanies} Orphaned`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  <Chip
                    icon={<BusinessIcon />}
                    label={`${groupedData.summary.totalEnterpriseAccounts} Enterprise`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* GROUPED VIEW - Companies grouped by iCloud user */}
          {viewMode === 'grouped' && (
            <Box>
              {groupedLoading && !groupedData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : groupedError ? (
                <Alert severity="error">Failed to load grouped companies: {groupedError.message}</Alert>
              ) : (
                <>
                  {/* Grouped Companies Cards */}
                  {groupedData?.groups?.map((group, idx) => (
                    <Card
                      key={group.cloudKitRecordID || `orphan-${idx}`}
                      sx={{
                        mb: 2,
                        border: group.isOrphaned
                          ? '1px solid'
                          : group.isMultiCompanyAccount
                            ? '2px solid'
                            : '1px solid',
                        borderColor: group.isOrphaned
                          ? 'warning.main'
                          : group.isMultiCompanyAccount
                            ? 'secondary.main'
                            : 'divider',
                        bgcolor: group.isOrphaned ? 'warning.50' : 'background.paper'
                      }}
                    >
                      {/* Group Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          bgcolor: group.isOrphaned
                            ? 'warning.light'
                            : group.isMultiCompanyAccount
                              ? 'rgba(156, 39, 176, 0.08)'
                              : 'grey.50',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: group.isOrphaned ? 'warning.200' : 'grey.100' }
                        }}
                        onClick={() => toggleGroupExpanded(group.cloudKitRecordID || `orphan-${idx}`)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* iCloud Icon */}
                          {group.isOrphaned ? (
                            <Tooltip title="No iCloud link - legacy or orphaned company">
                              <CloudOffIcon color="warning" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="iCloud User Account">
                              <CloudIcon color={group.isMultiCompanyAccount ? 'secondary' : 'action'} />
                            </Tooltip>
                          )}

                          {/* iCloud ID */}
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: group.isMultiCompanyAccount ? 'bold' : 'medium'
                              }}
                            >
                              {group.cloudKitIdShort || 'No iCloud Link'}
                            </Typography>
                            {group.cloudKitRecordID && (
                              <Tooltip title={`Full iCloud ID: ${group.cloudKitRecordID}`}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ cursor: 'help' }}
                                >
                                  Click to copy full ID
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>

                          {/* Multi-company badge */}
                          {group.isMultiCompanyAccount && (
                            <Chip
                              icon={<BusinessIcon sx={{ fontSize: '14px !important' }} />}
                              label={`${group.totalCompanyCount} companies`}
                              size="small"
                              color="secondary"
                            />
                          )}

                          {/* Enterprise badge */}
                          {group.isEnterpriseAccount && (
                            <Chip
                              label="Enterprise"
                              size="small"
                              color="primary"
                            />
                          )}

                          {/* Orphaned warning */}
                          {group.isOrphaned && (
                            <Tooltip title="This company was created without iCloud link. Consider data migration.">
                              <Chip
                                icon={<WarningIcon />}
                                label="No iCloud Link"
                                size="small"
                                color="warning"
                              />
                            </Tooltip>
                          )}
                        </Box>

                        {/* Right side stats */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="textSecondary">
                              {group.activeCompanyCount} active
                              {group.archivedCompanyCount > 0 && `, ${group.archivedCompanyCount} archived`}
                            </Typography>
                          </Box>
                          <ExpandMoreIcon
                            sx={{
                              transform: expandedGroups[group.cloudKitRecordID || `orphan-${idx}`]
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Expanded Company List */}
                      <Collapse in={expandedGroups[group.cloudKitRecordID || `orphan-${idx}`]}>
                        <Divider />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Company Name</TableCell>
                                <TableCell>Account No.</TableCell>
                                <TableCell>iCloud ID</TableCell>
                                <TableCell>Tier</TableCell>
                                <TableCell>Business Type</TableCell>
                                <TableCell align="center">Stores</TableCell>
                                <TableCell align="center">Team Members</TableCell>
                                <TableCell align="center">Devices</TableCell>
                                <TableCell>Last Active</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.companies.map((company) => {
                                const { storeCount, isMultiStore } = getStoreMeta(company);
                                const stores = company.storeStats?.stores || [];
                                const isExpanded = groupedViewExpandedStoreRows[company.companyId];
                                return (
                                  <React.Fragment key={company.companyId}>
                                    <TableRow
                                      hover
                                      onClick={() => handleRowClick(company.companyId)}
                                      sx={{
                                        cursor: 'pointer',
                                        opacity: company.isArchived ? 0.7 : 1,
                                        backgroundColor: company.isArchived ? 'action.hover' : 'inherit'
                                      }}
                                    >
                                      <TableCell>
                                        <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="body2">{company.name}</Typography>
                                            {company.isArchived && (
                                              <Chip
                                                icon={<ArchiveIcon />}
                                                label="Archived"
                                                size="small"
                                                color="warning"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                              />
                                            )}
                                            {isMultiStore && (
                                              <Chip
                                                icon={<StorefrontIcon sx={{ fontSize: '12px !important' }} />}
                                                label="Multi-Store"
                                                size="small"
                                                color="info"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                              />
                                            )}
                                          </Box>
                                          <Typography variant="caption" color="textSecondary">
                                            {company.companyId?.substring(0, 8)}...
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        {company.accountNumber ? (
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                            {company.accountNumber}
                                          </Typography>
                                        ) : (
                                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {company.cloudKitIdShort ? (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: 'monospace',
                                              backgroundColor: 'action.hover',
                                              padding: '2px 4px',
                                              borderRadius: 0.5,
                                            }}
                                          >
                                            {company.cloudKitIdShort}
                                          </Typography>
                                        ) : (
                                          <Chip
                                            icon={<LinkOffIcon />}
                                            label="Not linked"
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            sx={{ fontSize: '0.65rem', height: 18 }}
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={company.currentTier}
                                          color={tierColors[company.currentTier]}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">{company.businessType || '-'}</Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                          {isMultiStore && stores.length > 0 && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleGroupedViewStoreExpansion(company.companyId);
                                              }}
                                              sx={{ p: 0.25 }}
                                            >
                                              {isExpanded
                                                ? <KeyboardArrowDownIcon fontSize="small" />
                                                : <KeyboardArrowRightIcon fontSize="small" />
                                              }
                                            </IconButton>
                                          )}
                                          <Tooltip
                                            title={
                                              isMultiStore
                                                ? `Multi-store company with ${storeCount} locations. ${isExpanded ? 'Click arrow to collapse.' : 'Click arrow to expand.'} Click chip for full details.`
                                                : `Single-store company`
                                            }
                                          >
                                            <Chip
                                              icon={<StoreIcon />}
                                              label={storeCount}
                                              size="small"
                                              variant={isMultiStore ? 'filled' : 'outlined'}
                                              color={isMultiStore ? 'info' : 'default'}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openStoreDialog(company.companyId);
                                              }}
                                              sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 18 }}
                                            />
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2">
                                          {company.usageMetrics?.teamMembersCount || 0}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2">
                                          {company.deviceCount || company.connectedDevices?.length || 0}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {company.lastActiveDate
                                            ? format(new Date(company.lastActiveDate), 'MMM d, yyyy')
                                            : '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {company.isArchived ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <ArchiveIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                                            <Typography variant="caption" color="warning.main">Archived</Typography>
                                          </Box>
                                        ) : (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CircleIcon
                                              sx={{
                                                fontSize: 8,
                                                color: company.isOnlineNow ? 'success.main' : 'grey.400',
                                              }}
                                            />
                                            <Typography variant="caption">
                                              {company.isOnlineNow ? 'Online' : 'Offline'}
                                            </Typography>
                                          </Box>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    {/* Expandable sub-rows for multi-store companies in grouped view */}
                                    {isMultiStore && isExpanded && stores.length > 0 && (
                                      stores.map((store, idx) => {
                                        const isLast = idx === stores.length - 1;
                                        return (
                                          <TableRow
                                            key={`${company.companyId}-store-${store.id || idx}`}
                                            sx={{
                                              backgroundColor: 'grey.50',
                                              '&:hover': { backgroundColor: 'grey.100' },
                                            }}
                                          >
                                            <TableCell sx={{ py: 1, pl: 4 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ minWidth: 16 }}>
                                                  {isLast ? '└─' : '├─'}
                                                </Typography>
                                                <StorefrontIcon
                                                  fontSize="small"
                                                  color={store.isHeadquarters ? 'primary' : 'action'}
                                                />
                                                <Box>
                                                  <Typography variant="body2" fontWeight={store.isHeadquarters ? 600 : 400}>
                                                    {store.storeName || 'Unnamed Store'}
                                                    {store.isHeadquarters && (
                                                      <Chip label="HQ" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                                    )}
                                                  </Typography>
                                                  {store.storeCode && (
                                                    <Typography variant="caption" color="textSecondary">
                                                      Code: {store.storeCode}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            </TableCell>
                                            <TableCell colSpan={2} sx={{ py: 1 }}>
                                              {store.location?.city || store.location?.province ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <LocationOnIcon fontSize="small" color="action" />
                                                  <Typography variant="caption" color="textSecondary">
                                                    {[store.location?.city, store.location?.province].filter(Boolean).join(', ')}
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
                                              )}
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }} />
                                            <TableCell align="center" sx={{ py: 1 }}>
                                              <Tooltip title="Products at this store">
                                                <Chip
                                                  icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
                                                  label={store.stats?.productCount || 0}
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              </Tooltip>
                                            </TableCell>
                                            <TableCell align="center" sx={{ py: 1 }}>
                                              <Typography variant="body2" color="textSecondary">
                                                {store.stats?.teamMemberCount || '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }} />
                                            <TableCell sx={{ py: 1 }} />
                                            <TableCell sx={{ py: 1 }}>
                                              <Chip
                                                label={store.status || 'Active'}
                                                size="small"
                                                color={store.status === 'inactive' ? 'default' : 'success'}
                                                variant="outlined"
                                              />
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </Card>
                  ))}

                  {/* Grouped View Pagination */}
                  <TablePagination
                    component={Paper}
                    count={groupedData?.pagination?.total || 0}
                    page={groupedPage}
                    onPageChange={(e, newPage) => setGroupedPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setGroupedPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />

                  {/* Orphaned Companies Section */}
                  {orphanedData?.companies?.length > 0 && !onlyMultiCompany && (
                    <Card sx={{ mt: 3, border: '1px solid', borderColor: 'warning.main' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <WarningIcon color="warning" />
                          <Typography variant="h6">
                            Orphaned Companies (No iCloud Link)
                          </Typography>
                          <Chip
                            label={`${orphanedData.summary?.totalOrphaned || 0} total`}
                            size="small"
                            color="warning"
                          />
                        </Box>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          These companies have no iCloud account link. This may indicate:
                          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Legacy companies created before iCloud linking was implemented</li>
                            <li>Companies created through old app versions</li>
                            <li>Users who weren't signed into iCloud</li>
                          </ul>
                        </Alert>

                        {/* Breakdown by reason */}
                        {orphanedData.summary?.byReason && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            {Object.entries(orphanedData.summary.byReason).map(([reason, count]) => (
                              <Chip
                                key={reason}
                                label={`${reason.replace(/_/g, ' ')}: ${count}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}

                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Company</TableCell>
                                <TableCell>Tier</TableCell>
                                <TableCell>Registered</TableCell>
                                <TableCell>Last Active</TableCell>
                                <TableCell>Likely Reason</TableCell>
                                <TableCell>Priority</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orphanedData.companies.slice(0, 10).map((company) => (
                                <TableRow
                                  key={company.companyId}
                                  hover
                                  onClick={() => handleRowClick(company.companyId)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2">{company.name}</Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {company.companyId?.substring(0, 8)}...
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={company.currentTier}
                                      color={tierColors[company.currentTier]}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {company.registrationDate
                                      ? format(new Date(company.registrationDate), 'MMM d, yyyy')
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {company.lastActiveDate
                                      ? format(new Date(company.lastActiveDate), 'MMM d, yyyy')
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                      {company.analysis?.likelyReason?.replace(/_/g, ' ') || 'Unknown'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={company.analysis?.priority || 'low'}
                                      size="small"
                                      color={
                                        company.analysis?.priority === 'high' ? 'error' :
                                        company.analysis?.priority === 'medium' ? 'warning' :
                                        'default'
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </Box>
          )}

          {/* LIST VIEW - Companies Table */}
          {viewMode === 'list' && (
            <>
              {/* Tier Distribution for list view */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Tier Distribution</Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Free</Typography>
                      <Typography variant="h5">{stats.byTier?.free || 0}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Professional</Typography>
                      <Typography variant="h5" color="primary">{stats.byTier?.professional || 0}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Enterprise</Typography>
                      <Typography variant="h5" color="secondary">{stats.byTier?.enterprise || 0}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

          {/* Companies - Mobile Card Layout */}
          {isMobile ? (
            <Box>
              {companies.map((company) => {
                const siblingCount = Math.max(company.siblingCompanyCount || 1, 1);
                const isMultiCompany = siblingCount > 1 || company.isMultiCompanyAccount;
                const { storeCount } = getStoreMeta(company);
                return (
                  <Card
                    key={company.companyId}
                    onClick={() => handleRowClick(company.companyId)}
                    sx={{
                      mb: 1.5,
                      cursor: 'pointer',
                      opacity: company.isArchived ? 0.7 : 1,
                      borderLeft: isMultiCompany ? '3px solid rgba(156, 39, 176, 0.5)' : 'none',
                      '&:active': { transform: 'scale(0.99)' },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Row 1: Company Name + Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {company.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {company.companyId?.substring(0, 8)}...
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          {company.isArchived ? (
                            <Chip icon={<ArchiveIcon />} label="Archived" size="small" color="warning" sx={{ fontSize: '0.7rem' }} />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CircleIcon sx={{ fontSize: 10, color: company.isOnlineNow ? 'success.main' : 'grey.400' }} />
                              <Typography variant="caption">{company.isOnlineNow ? 'Online' : 'Offline'}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Row 2: Badges */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                        <Chip label={company.currentTier} color={tierColors[company.currentTier]} size="small" />
                        {company.businessType && (
                          <Chip label={company.businessType} size="small" variant="outlined" />
                        )}
                        {isMultiCompany && (
                          <Chip icon={<PersonIcon sx={{ fontSize: '14px !important' }} />} label="Multi-Company" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                        {company.isFraudSuspect && (
                          <Chip label="Fraud" size="small" color="error" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </Box>

                      {/* Row 3: Account Number + iCloud */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block">Account No.</Typography>
                          {company.accountNumber ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, backgroundColor: 'action.hover', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block' }}>
                              {company.accountNumber}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block">iCloud ID</Typography>
                          {company.cloudKitIdShort ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {company.cloudKitIdShort}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinkOffIcon fontSize="inherit" color="warning" />
                              <Typography variant="caption" color="warning.main">Not linked</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Row 4: Metrics */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip icon={<StoreIcon />} label={storeCount} size="small" variant="outlined" sx={{ flex: 1, justifyContent: 'flex-start' }} />
                        <Chip icon={<PeopleIcon />} label={company.usageMetrics?.teamMembersCount || 0} size="small" variant="outlined" sx={{ flex: 1, justifyContent: 'flex-start' }} />
                        <Chip icon={<DevicesIcon />} label={company.deviceCount || company.connectedDevices?.length || 0} size="small" variant="outlined" sx={{ flex: 1, justifyContent: 'flex-start' }} />
                      </Box>

                      {/* Row 5: Dates */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="textSecondary">
                          Registered: {company.registrationDate ? format(new Date(company.registrationDate), 'MMM d, yyyy') : '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Active: {company.lastActiveDate ? format(new Date(company.lastActiveDate), 'MMM d, yyyy') : '-'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              <TablePagination
                component="div"
                count={pagination.total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </Box>
          ) : (
          /* Companies Table - Desktop */
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Company Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Unique account number for billing and support reference">
                      <span>Account No.</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="iCloud Account ID (identifies user across devices)">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CloudIcon fontSize="small" />
                        <span>iCloud ID</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Business Type</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Number of stores/branches for this company">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <StoreIcon fontSize="small" />
                        <span>Stores</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Total team members across all stores and devices">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PeopleIcon fontSize="small" />
                        <span>Team Members</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Unique devices with the app installed">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <DevicesIcon fontSize="small" />
                        <span>Devices</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'registrationDate'}
                      direction={orderBy === 'registrationDate' ? order : 'asc'}
                      onClick={() => handleSort('registrationDate')}
                    >
                      Registered
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'lastActiveDate'}
                      direction={orderBy === 'lastActiveDate' ? order : 'asc'}
                      onClick={() => handleSort('lastActiveDate')}
                    >
                      Last Active
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => {
                  const siblingCount = Math.max(company.siblingCompanyCount || 1, 1);
                  const isMultiCompany = siblingCount > 1 || company.isMultiCompanyAccount;
                  const { storeCount, isMultiStore } = getStoreMeta(company);
                  return (
                    <React.Fragment key={company.companyId}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(company.companyId)}
                      sx={{
                        cursor: 'pointer',
                        // Slightly grey out archived companies
                        opacity: company.isArchived ? 0.7 : 1,
                        backgroundColor: company.isArchived
                          ? 'action.hover'
                          : isMultiCompany
                            ? 'rgba(156, 39, 176, 0.04)' // Light purple for enterprise multi-company
                            : 'inherit',
                        // Add left border for multi-company accounts
                        borderLeft: isMultiCompany
                          ? '3px solid rgba(156, 39, 176, 0.5)'
                          : 'none',
                        '&:hover': {
                          backgroundColor: isMultiCompany
                            ? 'rgba(156, 39, 176, 0.08)'
                            : undefined
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1">{company.name}</Typography>
                            {company.isArchived && (
                              <Chip
                                icon={<ArchiveIcon />}
                                label={`Archived ${company.archivedAt ? format(new Date(company.archivedAt), 'MMM d, yyyy') : ''}`}
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                            {company.isFraudSuspect && (
                              <Chip
                                label="Fraud Suspect"
                                size="small"
                                color="error"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                            {isMultiCompany && (
                              <Tooltip title={`Enterprise multi-company account (${siblingCount} companies under one iCloud user)`}>
                                <Chip
                                  icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Multi-Company"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {company.companyId?.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {company.accountNumber ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: 'action.hover',
                              padding: '2px 6px',
                              borderRadius: 1,
                              fontWeight: 500
                            }}
                          >
                            {company.accountNumber}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.cloudKitIdShort ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Click to view all companies for this iCloud user">
                              <Typography
                                variant="caption"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openIcloudCompaniesDialog(company.displayCloudKitRecordID);
                                }}
                                sx={{
                                  fontFamily: 'monospace',
                                  backgroundColor: 'action.hover',
                                  padding: '2px 6px',
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textDecorationColor: 'divider'
                                }}
                              >
                                {company.cloudKitIdShort}
                              </Typography>
                            </Tooltip>
                            <Tooltip
                              title={
                                isMultiCompany
                                  ? `This iCloud user owns ${siblingCount} companies (enterprise multi-company)`
                                  : 'Single company tied to this iCloud user'
                              }
                            >
                              <Chip
                                icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                label={`${siblingCount} ${siblingCount === 1 ? 'Company' : 'Companies'}`}
                                size="small"
                                color={isMultiCompany ? 'secondary' : 'default'}
                                variant={isMultiCompany ? 'filled' : 'outlined'}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="CloudKit ID not linked - company may have been created before iCloud linking was implemented">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinkOffIcon fontSize="inherit" color="warning" />
                              <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                Not linked
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    <TableCell>
                      <Chip
                        label={company.currentTier}
                        color={tierColors[company.currentTier]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{company.businessType || '-'}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {isMultiStore && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleStoreRowExpansion(company.companyId, isMultiStore);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            {expandedStoreRows[company.companyId]
                              ? <KeyboardArrowDownIcon fontSize="small" />
                              : <KeyboardArrowRightIcon fontSize="small" />
                            }
                          </IconButton>
                        )}
                        <Tooltip
                          title={
                            isMultiStore
                              ? `Multi-store company with ${storeCount} locations. ${expandedStoreRows[company.companyId] ? 'Click arrow to collapse.' : 'Click arrow to expand.'} Click chip to view full details.`
                              : `Single-store company. Click to view store details.`
                          }
                        >
                          <Chip
                            icon={<StoreIcon />}
                            label={storeCount}
                            size="small"
                            variant={isMultiStore ? 'filled' : 'outlined'}
                            color={isMultiStore ? 'info' : 'default'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openStoreDialog(company.companyId);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${company.usageMetrics?.teamMembersCount || 0} team members across all stores`}>
                        <Chip
                          icon={<PeopleIcon />}
                          label={company.usageMetrics?.teamMembersCount || 0}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${company.deviceCount || company.connectedDevices?.length || 0} devices`}>
                        <Chip
                          icon={<DevicesIcon />}
                          label={company.deviceCount || company.connectedDevices?.length || 0}
                          size="small"
                          variant="outlined"
                          color={company.deviceCount > 1 || (company.connectedDevices?.length || 0) > 1 ? 'primary' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {company.registrationDate
                        ? format(new Date(company.registrationDate), 'MMM d, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {company.lastActiveDate
                        ? format(new Date(company.lastActiveDate), 'MMM d, yyyy HH:mm')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {company.isArchived ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ArchiveIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                          <Typography variant="body2" color="warning.main">Archived</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircleIcon
                            sx={{
                              fontSize: 12,
                              color: company.isOnlineNow ? 'success.main' : 'grey.400',
                            }}
                          />
                          {company.isOnlineNow ? 'Online' : 'Offline'}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Expandable sub-rows for multi-store companies - each store as a separate TableRow */}
                  {isMultiStore && expandedStoreRows[company.companyId] && (
                    <>
                      {!storeRowData[company.companyId] ? (
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell colSpan={10} sx={{ py: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="textSecondary">Loading store details...</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : storeRowData[company.companyId].error ? (
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell colSpan={10} sx={{ py: 1 }}>
                            <Alert severity="error" sx={{ mx: 4 }}>
                              {storeRowData[company.companyId].error}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      ) : storeRowData[company.companyId].stores.length === 0 ? (
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell colSpan={10} sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ pl: 4 }}>
                              No stores synced yet. Stores will appear once synced from the iOS app.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        storeRowData[company.companyId].stores.map((store, idx) => {
                          const teamMembersData = storeRowData[company.companyId].teamMembersPerStore || [];
                          const storeTeamMembers = teamMembersData.find(t =>
                            t.storeId === store.storeId || t.storeName === store.storeName
                          );
                          const teamCount = storeTeamMembers?.userCount || storeTeamMembers?.count || 0;
                          const isLast = idx === storeRowData[company.companyId].stores.length - 1;

                          return (
                            <TableRow
                              key={`${company.companyId}-store-${store.id || idx}`}
                              sx={{
                                backgroundColor: 'grey.50',
                                '&:hover': { backgroundColor: 'grey.100' },
                              }}
                            >
                              {/* Store Name - aligns with Company Name column */}
                              <TableCell sx={{ py: 1, pl: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 16 }}>
                                    {isLast ? '└─' : '├─'}
                                  </Typography>
                                  <StorefrontIcon
                                    fontSize="small"
                                    color={store.isHeadquarters ? 'primary' : 'action'}
                                  />
                                  <Box>
                                    <Typography variant="body2" fontWeight={store.isHeadquarters ? 600 : 400}>
                                      {store.storeName || 'Unnamed Store'}
                                      {store.isHeadquarters && (
                                        <Chip label="HQ" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                      )}
                                    </Typography>
                                    {store.storeCode && (
                                      <Typography variant="caption" color="textSecondary">
                                        Code: {store.storeCode}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              {/* Location - aligns with iCloud ID column */}
                              <TableCell sx={{ py: 1 }}>
                                {store.location?.city || store.location?.province ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="textSecondary">
                                      {[store.location?.city, store.location?.province].filter(Boolean).join(', ')}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              {/* Empty - aligns with Tier column */}
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="caption" color="textSecondary">—</Typography>
                              </TableCell>
                              {/* Store Type - aligns with Business Type column */}
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  {store.storeType || '—'}
                                </Typography>
                              </TableCell>
                              {/* Products - aligns with Stores column */}
                              <TableCell align="center" sx={{ py: 1 }}>
                                <Tooltip title="Products at this store">
                                  <Chip
                                    icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
                                    label={store.stats?.productCount || 0}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              </TableCell>
                              {/* Team Members - aligns with Team Members column */}
                              <TableCell align="center" sx={{ py: 1 }}>
                                <Tooltip title="Team members at this store">
                                  <Chip
                                    icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
                                    label={teamCount}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              </TableCell>
                              {/* Devices - aligns with Devices column */}
                              <TableCell align="center" sx={{ py: 1 }}>
                                <Typography variant="caption" color="textSecondary">—</Typography>
                              </TableCell>
                              {/* Created - aligns with Registered column */}
                              <TableCell sx={{ py: 1 }}>
                                {store.createdAt ? (
                                  <Typography variant="caption" color="textSecondary">
                                    {format(new Date(store.createdAt), 'MMM d, yyyy')}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">—</Typography>
                                )}
                              </TableCell>
                              {/* Empty - aligns with Last Active column */}
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="caption" color="textSecondary">—</Typography>
                              </TableCell>
                              {/* Status - aligns with Status column */}
                              <TableCell sx={{ py: 1 }}>
                                <Chip
                                  label={store.status || 'Active'}
                                  size="small"
                                  color={store.status === 'inactive' ? 'default' : 'success'}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </>
                  )}
                  </React.Fragment>
                );
              })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pagination.total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </TableContainer>
          )}
            </>
          )}
        </Box>
      )}

      {/* Real-Time Tab */}
      {activeTab === 2 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    <Typography variant="h6">Online Now</Typography>
                  </Box>
                  <Typography variant="h2" color="success.main">{onlineData?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Companies actively using the app
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DevicesIcon color="action" />
                    <Typography variant="body2">
                      {onlineData?.totalDevices || 0} devices connected
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Online Companies by Tier</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                        <Typography variant="h3">{onlineData?.byTier?.free || 0}</Typography>
                        <Typography variant="body2">Free</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="h3">{onlineData?.byTier?.professional || 0}</Typography>
                        <Typography variant="body2">Professional</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 2 }}>
                        <Typography variant="h3">{onlineData?.byTier?.enterprise || 0}</Typography>
                        <Typography variant="body2">Enterprise</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Currently Online Companies</Typography>
                  {onlineData?.companies?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Company Name</TableCell>
                            <TableCell>Tier</TableCell>
                            <TableCell>Business Type</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Team members">
                                <PeopleIcon fontSize="small" />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Connected devices">
                                <DevicesIcon fontSize="small" />
                              </Tooltip>
                            </TableCell>
                            <TableCell>Last Heartbeat</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {onlineData.companies.map((company) => (
                            <TableRow key={company.companyId} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircleIcon sx={{ fontSize: 10, color: 'success.main' }} />
                                  {company.name}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={company.currentTier}
                                  color={tierColors[company.currentTier]}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{company.businessType || '-'}</TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {company.teamMembersCount || company.usageMetrics?.teamMembersCount || 0}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color={company.deviceCount > 1 ? 'primary' : 'textSecondary'}>
                                  {company.deviceCount || company.connectedDevices?.length || 0}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {company.lastHeartbeat
                                  ? format(new Date(company.lastHeartbeat), 'HH:mm:ss')
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No companies are online at the moment</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* FRAUD MANAGEMENT TAB (activeTab === 3)                            */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 3 && (
        <Box>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="error" />
              <Typography variant="h6">Fraud Management</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<FlagIcon />}
                size="small"
                onClick={() => { refetchFraudStats(); refetchFraudQueue(); }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<GppBadIcon />}
                size="small"
                onClick={() => companiesAPI.exportFraudCSV().catch(err => alert(err.message))}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* ── Fraud Statistics Dashboard ── */}
          {fraudStatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderLeft: '4px solid #f44336' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">Fraud Suspects</Typography>
                      <Typography variant="h4" color="error">{fraudStatsData?.data?.totalFraudSuspects ?? '-'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">Total Start-Fresh Events</Typography>
                      <Typography variant="h4" color="warning.main">{fraudStatsData?.data?.totalArchivedCompanies ?? '-'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">Users w/ History</Typography>
                      <Typography variant="h4" color="primary">{fraudStatsData?.data?.usersWithStartFreshHistory ?? '-'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">Unresolved Flags</Typography>
                      <Typography variant="h4" color="secondary">{(fraudStatsData?.data?.recentFlags || []).length}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* ── Top Offenders ── */}
              {(fraudStatsData?.data?.topOffenders || []).length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonOffIcon color="error" fontSize="small" /> Top Offenders (most archived companies)
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>iCloud ID (truncated)</TableCell>
                            <TableCell align="center">Archived Companies</TableCell>
                            <TableCell>History</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(fraudStatsData.data.topOffenders || []).map((o, i) => (
                            <TableRow key={i} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                {String(o._id || '').substring(0, 16)}...
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={o.archivedCount} size="small" color="error" />
                              </TableCell>
                              <TableCell>
                                {(o.archivedCompanies || []).map((c, ci) => (
                                  <Typography key={ci} variant="caption" display="block" color="textSecondary">
                                    {c.name || c.companyId} — {c.tier} — {c.archivedAt ? new Date(c.archivedAt).toLocaleDateString() : '?'}
                                  </Typography>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* ── Recent Unresolved Flags ── */}
              {(fraudStatsData?.data?.recentFlags || []).length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon color="warning" fontSize="small" /> Recent Unresolved Flags (latest 20)
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Detected</TableCell>
                            <TableCell>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(fraudStatsData.data.recentFlags || []).map((f, i) => (
                            <TableRow key={i} hover>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                                  onClick={() => handleRowClick(f.companyId)}
                                >
                                  {f.name || f.companyId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={f.fraudFlag?.type || '-'} size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="caption">
                                  {f.fraudFlag?.detectedAt ? new Date(f.fraudFlag.detectedAt).toLocaleString() : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="textSecondary">{f.fraudFlag?.details || '-'}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* ── Fraud Review Queue ── */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GppBadIcon color="error" fontSize="small" />
                Fraud Review Queue — sorted by Start Fresh count ↓
                {!fraudQueueLoading && (
                  <Chip
                    label={`${fraudQueueData?.companies?.length ?? 0} open`}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>

              {fraudQueueLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
              ) : (fraudQueueData?.companies || []).length === 0 ? (
                <Alert severity="success">No companies in the fraud review queue 🎉</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Company</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Tier</TableCell>
                        <TableCell align="center">Start Fresh</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Flags</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(fraudQueueData.companies || []).map((c) => (
                        <TableRow key={c.companyId} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ cursor: 'pointer', color: 'primary.main' }}
                              onClick={() => handleRowClick(c.companyId)}
                            >
                              {c.name || c.companyId}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {c.accountNumber || c.companyId?.substring(0, 8)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{c.email || '-'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={c.currentTier || 'free'} size="small" color={tierColors[c.currentTier] || 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={c.startFreshCount || 0} size="small" color="error" />
                          </TableCell>
                          <TableCell align="center">
                            {c.isBanned ? (
                              <Chip label="Banned" size="small" color="error" icon={<BlockIcon />} />
                            ) : c.subscriptionStatus === 'suspended' ? (
                              <Chip label="Suspended" size="small" color="warning" />
                            ) : (
                              <Chip label="Active" size="small" color="success" />
                            )}
                          </TableCell>
                          <TableCell>
                            {(c.fraudFlags || []).filter(f => !f.resolved).map((f, i) => (
                              <Chip key={i} label={f.type} size="small" color="warning" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.6rem' }} />
                            ))}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              <Tooltip title="View company details">
                                <IconButton size="small" onClick={() => handleRowClick(c.companyId)}>
                                  <BusinessIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Resolve fraud flag">
                                <IconButton size="small" color="success" onClick={() => {
                                  setSelectedCompanyDetail(c);
                                  setResolveFraudNotes('');
                                  setResolveFraudOpen(true);
                                }}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Force-downgrade to Free">
                                <IconButton size="small" color="warning" onClick={() => {
                                  setSelectedCompanyDetail(c);
                                  setForceDowngradeReason('');
                                  setForceDowngradeOpen(true);
                                }}>
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Suspend account">
                                <IconButton size="small" color="error" onClick={() => {
                                  setSelectedCompanyDetail(c);
                                  setSuspendReason('');
                                  setSuspendOpen(true);
                                }}>
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently ban iCloud user">
                                <IconButton size="small" sx={{ color: 'grey.700' }} onClick={() => {
                                  setSelectedCompanyDetail(c);
                                  setBanReason('');
                                  setBanOpen(true);
                                }}>
                                  <PersonOffIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

		{/* iCloud User Companies Dialog (grouped view) */}
		<Dialog open={icloudDialogOpen} onClose={closeIcloudCompaniesDialog} maxWidth="md" fullWidth>
		  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
		    <CloudIcon color="primary" />
		    iCloud User Companies
		    {selectedCloudKitRecordID && (
		      <Typography variant="caption" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
		        {String(selectedCloudKitRecordID).substring(0, 16)}...
		      </Typography>
		    )}
		  </DialogTitle>
		  <DialogContent>
		    {icloudCompaniesLoading ? (
		      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
		        <CircularProgress />
		      </Box>
		    ) : icloudCompaniesError ? (
		      <Alert severity="error">{icloudCompaniesError?.message || 'Failed to load iCloud user companies'}</Alert>
		    ) : (
		      <Box sx={{ pt: 1 }}>
		        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
		          Full iCloud ID
		        </Typography>
		        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
		          {icloudCompaniesData?.fullCloudKitRecordID || selectedCloudKitRecordID || '-'}
		        </Typography>

		        <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
		          <CardContent>
		            <Typography variant="h6" gutterBottom>
		              Active Companies ({icloudCompaniesData?.activeCompanyCount || 0})
		            </Typography>
		            {(icloudCompaniesData?.activeCompanies?.length || 0) > 0 ? (
		              <TableContainer component={Paper}>
		                <Table size="small">
		                  <TableHead>
		                    <TableRow>
		                      <TableCell>Company</TableCell>
		                      <TableCell>Tier</TableCell>
		                      <TableCell>Business Type</TableCell>
		                      <TableCell align="center">Stores</TableCell>
		                      <TableCell align="center">Devices</TableCell>
		                      <TableCell>Last Active</TableCell>
		                    </TableRow>
		                  </TableHead>
		                  <TableBody>
		                    {icloudCompaniesData.activeCompanies.map((c) => {
		                      const { storeCount, isMultiStore } = getStoreMeta(c);
		                      const stores = c.storeStats?.stores || [];
		                      const isExpanded = icloudDialogExpandedRows[c.companyId];
		                      return (
		                        <React.Fragment key={c.companyId}>
		                          <TableRow
		                            hover
		                            sx={{ cursor: 'pointer' }}
		                            onClick={() => {
		                              closeIcloudCompaniesDialog();
		                              handleRowClick(c.companyId);
		                            }}
		                          >
		                            <TableCell>
		                              <Box>
		                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
		                                  <Typography variant="body2" fontWeight="medium">{c.name || c.companyId}</Typography>
		                                  {isMultiStore && (
		                                    <Chip
		                                      icon={<StorefrontIcon sx={{ fontSize: '12px !important' }} />}
		                                      label="Multi-Store"
		                                      size="small"
		                                      color="info"
		                                      sx={{ fontSize: '0.65rem', height: 18 }}
		                                    />
		                                  )}
		                                </Box>
		                                <Typography variant="caption" color="textSecondary">{c.companyId?.substring(0, 8)}...</Typography>
		                              </Box>
		                            </TableCell>
		                            <TableCell>
		                              <Chip label={c.currentTier} size="small" color={tierColors[c.currentTier]} />
		                            </TableCell>
		                            <TableCell>{c.businessType || '-'}</TableCell>
		                            <TableCell align="center">
		                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
		                                {isMultiStore && stores.length > 0 && (
		                                  <IconButton
		                                    size="small"
		                                    onClick={(e) => {
		                                      e.preventDefault();
		                                      e.stopPropagation();
		                                      toggleIcloudDialogStoreExpansion(c.companyId);
		                                    }}
		                                    sx={{ p: 0.25 }}
		                                  >
		                                    {isExpanded
		                                      ? <KeyboardArrowDownIcon fontSize="small" />
		                                      : <KeyboardArrowRightIcon fontSize="small" />
		                                    }
		                                  </IconButton>
		                                )}
		                                <Tooltip
		                                  title={
		                                    isMultiStore
		                                      ? `Multi-store company with ${storeCount} locations. ${isExpanded ? 'Click arrow to collapse.' : 'Click arrow to expand.'} Click chip for full details.`
		                                      : `Single-store company`
		                                  }
		                                >
		                                  <Chip
		                                    icon={<StoreIcon />}
		                                    label={storeCount}
		                                    size="small"
		                                    variant={isMultiStore ? 'filled' : 'outlined'}
		                                    color={isMultiStore ? 'info' : 'default'}
		                                    onClick={(e) => {
		                                      e.preventDefault();
		                                      e.stopPropagation();
		                                      closeIcloudCompaniesDialog();
		                                      openStoreDialog(c.companyId);
		                                    }}
		                                    sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
		                                  />
		                                </Tooltip>
		                              </Box>
		                            </TableCell>
		                            <TableCell align="center">
		                              <Chip label={c.deviceCount || 0} size="small" variant="outlined" />
		                            </TableCell>
		                            <TableCell>
		                              {c.lastActiveDate ? format(new Date(c.lastActiveDate), 'MMM d, yyyy HH:mm') : '-'}
		                            </TableCell>
		                          </TableRow>
		                          {/* Expandable sub-rows for multi-store companies */}
		                          {isMultiStore && isExpanded && stores.length > 0 && (
		                            stores.map((store, idx) => {
		                              const isLast = idx === stores.length - 1;
		                              return (
		                                <TableRow
		                                  key={`${c.companyId}-store-${store.id || idx}`}
		                                  sx={{
		                                    backgroundColor: 'grey.50',
		                                    '&:hover': { backgroundColor: 'grey.100' },
		                                  }}
		                                >
		                                  <TableCell sx={{ py: 1, pl: 4 }}>
		                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		                                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 16 }}>
		                                        {isLast ? '└─' : '├─'}
		                                      </Typography>
		                                      <StorefrontIcon
		                                        fontSize="small"
		                                        color={store.isHeadquarters ? 'primary' : 'action'}
		                                      />
		                                      <Box>
		                                        <Typography variant="body2" fontWeight={store.isHeadquarters ? 600 : 400}>
		                                          {store.storeName || 'Unnamed Store'}
		                                          {store.isHeadquarters && (
		                                            <Chip label="HQ" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
		                                          )}
		                                        </Typography>
		                                        {store.storeCode && (
		                                          <Typography variant="caption" color="textSecondary">
		                                            Code: {store.storeCode}
		                                          </Typography>
		                                        )}
		                                      </Box>
		                                    </Box>
		                                  </TableCell>
		                                  <TableCell sx={{ py: 1 }}>
		                                    {store.location?.city || store.location?.province ? (
		                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
		                                        <LocationOnIcon fontSize="small" color="action" />
		                                        <Typography variant="caption" color="textSecondary">
		                                          {[store.location?.city, store.location?.province].filter(Boolean).join(', ')}
		                                        </Typography>
		                                      </Box>
		                                    ) : (
		                                      <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
		                                    )}
		                                  </TableCell>
		                                  <TableCell sx={{ py: 1 }}>
		                                    <Typography variant="caption" color="textSecondary">
		                                      {store.storeType || '—'}
		                                    </Typography>
		                                  </TableCell>
		                                  <TableCell align="center" sx={{ py: 1 }}>
		                                    <Tooltip title="Products at this store">
		                                      <Chip
		                                        icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
		                                        label={store.stats?.productCount || 0}
		                                        size="small"
		                                        variant="outlined"
		                                      />
		                                    </Tooltip>
		                                  </TableCell>
		                                  <TableCell sx={{ py: 1 }}>
		                                    <Typography variant="caption" color="textSecondary">—</Typography>
		                                  </TableCell>
		                                  <TableCell sx={{ py: 1 }}>
		                                    <Chip
		                                      label={store.status || 'Active'}
		                                      size="small"
		                                      color={store.status === 'inactive' ? 'default' : 'success'}
		                                      variant="outlined"
		                                    />
		                                  </TableCell>
		                                </TableRow>
		                              );
		                            })
		                          )}
		                        </React.Fragment>
		                      );
		                    })}
		                  </TableBody>
		                </Table>
		              </TableContainer>
		            ) : (
		              <Alert severity="info">No active companies found for this iCloud user.</Alert>
		            )}
		          </CardContent>
		        </Card>

		        <Card sx={{ bgcolor: 'grey.50' }}>
		          <CardContent>
		            <Typography variant="h6" gutterBottom>
		              Archived Companies ({icloudCompaniesData?.archivedCompanyCount || 0})
		            </Typography>
		            {(icloudCompaniesData?.archivedCompanies?.length || 0) > 0 ? (
		              <TableContainer component={Paper}>
		                <Table size="small">
		                  <TableHead>
		                    <TableRow>
		                      <TableCell>Company</TableCell>
		                      <TableCell>Tier</TableCell>
		                      <TableCell align="center">Stores</TableCell>
		                      <TableCell>Archived</TableCell>
		                      <TableCell>Reason</TableCell>
		                    </TableRow>
		                  </TableHead>
		                  <TableBody>
		                    {icloudCompaniesData.archivedCompanies.map((c) => {
		                      const { storeCount, isMultiStore } = getStoreMeta(c);
		                      const stores = c.storeStats?.stores || [];
		                      const isExpanded = icloudDialogExpandedRows[c.companyId];
		                      return (
		                        <React.Fragment key={c.companyId}>
		                          <TableRow
		                            hover
		                            sx={{ cursor: 'pointer', opacity: 0.85 }}
		                            onClick={() => {
		                              closeIcloudCompaniesDialog();
		                              handleRowClick(c.companyId);
		                            }}
		                          >
		                            <TableCell>
		                              <Box>
		                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
		                                  <Typography variant="body2" fontWeight="medium">{c.name || c.companyId}</Typography>
		                                  {isMultiStore && (
		                                    <Chip
		                                      icon={<StorefrontIcon sx={{ fontSize: '12px !important' }} />}
		                                      label="Multi-Store"
		                                      size="small"
		                                      color="info"
		                                      sx={{ fontSize: '0.65rem', height: 18 }}
		                                    />
		                                  )}
		                                </Box>
		                                <Typography variant="caption" color="textSecondary">{c.companyId?.substring(0, 8)}...</Typography>
		                              </Box>
		                            </TableCell>
		                            <TableCell>
		                              <Chip label={c.currentTier} size="small" color={tierColors[c.currentTier]} />
		                            </TableCell>
		                            <TableCell align="center">
		                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
		                                {isMultiStore && stores.length > 0 && (
		                                  <IconButton
		                                    size="small"
		                                    onClick={(e) => {
		                                      e.preventDefault();
		                                      e.stopPropagation();
		                                      toggleIcloudDialogStoreExpansion(c.companyId);
		                                    }}
		                                    sx={{ p: 0.25 }}
		                                  >
		                                    {isExpanded
		                                      ? <KeyboardArrowDownIcon fontSize="small" />
		                                      : <KeyboardArrowRightIcon fontSize="small" />
		                                    }
		                                  </IconButton>
		                                )}
		                                <Tooltip
		                                  title={
		                                    isMultiStore
		                                      ? `Multi-store company with ${storeCount} locations. ${isExpanded ? 'Click arrow to collapse.' : 'Click arrow to expand.'} Click chip for full details.`
		                                      : `Single-store company`
		                                  }
		                                >
		                                  <Chip
		                                    icon={<StoreIcon />}
		                                    label={storeCount}
		                                    size="small"
		                                    variant={isMultiStore ? 'filled' : 'outlined'}
		                                    color={isMultiStore ? 'info' : 'default'}
		                                    onClick={(e) => {
		                                      e.preventDefault();
		                                      e.stopPropagation();
		                                      closeIcloudCompaniesDialog();
		                                      openStoreDialog(c.companyId);
		                                    }}
		                                    sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
		                                  />
		                                </Tooltip>
		                              </Box>
		                            </TableCell>
		                            <TableCell>
		                              {c.archivedAt ? format(new Date(c.archivedAt), 'MMM d, yyyy') : '-'}
		                            </TableCell>
		                            <TableCell>{c.archivedReason || '-'}</TableCell>
		                          </TableRow>
		                          {/* Expandable sub-rows for multi-store archived companies */}
		                          {isMultiStore && isExpanded && stores.length > 0 && (
		                            stores.map((store, idx) => {
		                              const isLast = idx === stores.length - 1;
		                              return (
		                                <TableRow
		                                  key={`${c.companyId}-store-${store.id || idx}`}
		                                  sx={{
		                                    backgroundColor: 'grey.100',
		                                    opacity: 0.85,
		                                    '&:hover': { backgroundColor: 'grey.200' },
		                                  }}
		                                >
		                                  <TableCell sx={{ py: 1, pl: 4 }}>
		                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		                                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 16 }}>
		                                        {isLast ? '└─' : '├─'}
		                                      </Typography>
		                                      <StorefrontIcon
		                                        fontSize="small"
		                                        color={store.isHeadquarters ? 'primary' : 'action'}
		                                      />
		                                      <Box>
		                                        <Typography variant="body2" fontWeight={store.isHeadquarters ? 600 : 400}>
		                                          {store.storeName || 'Unnamed Store'}
		                                          {store.isHeadquarters && (
		                                            <Chip label="HQ" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
		                                          )}
		                                        </Typography>
		                                        {store.storeCode && (
		                                          <Typography variant="caption" color="textSecondary">
		                                            Code: {store.storeCode}
		                                          </Typography>
		                                        )}
		                                      </Box>
		                                    </Box>
		                                  </TableCell>
		                                  <TableCell sx={{ py: 1 }}>
		                                    {store.location?.city || store.location?.province ? (
		                                      <Typography variant="caption" color="textSecondary">
		                                        {[store.location?.city, store.location?.province].filter(Boolean).join(', ')}
		                                      </Typography>
		                                    ) : (
		                                      <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
		                                    )}
		                                  </TableCell>
		                                  <TableCell align="center" sx={{ py: 1 }}>
		                                    <Tooltip title="Products at this store">
		                                      <Chip
		                                        icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
		                                        label={store.stats?.productCount || 0}
		                                        size="small"
		                                        variant="outlined"
		                                      />
		                                    </Tooltip>
		                                  </TableCell>
		                                  <TableCell colSpan={2} sx={{ py: 1 }}>
		                                    <Chip
		                                      label={store.status || 'Active'}
		                                      size="small"
		                                      color={store.status === 'inactive' ? 'default' : 'success'}
		                                      variant="outlined"
		                                    />
		                                  </TableCell>
		                                </TableRow>
		                              );
		                            })
		                          )}
		                        </React.Fragment>
		                      );
		                    })}
		                  </TableBody>
		                </Table>
		              </TableContainer>
		            ) : (
		              <Alert severity="info">No archived companies found for this iCloud user.</Alert>
		            )}
		          </CardContent>
		        </Card>
		      </Box>
		    )}
		  </DialogContent>
		  <DialogActions>
		    <Button onClick={closeIcloudCompaniesDialog}>Close</Button>
		  </DialogActions>
		</Dialog>

      {/* Company Stores/Branches Dialog */}
      <Dialog open={storeDialogOpen} onClose={closeStoreDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <AccountTreeIcon color="primary" />
          Stores and Branches
          {storeDialogData?.company?.name && (
            <Typography variant="subtitle2" sx={{ ml: 'auto', color: 'text.secondary' }}>
              {storeDialogData.company.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {storeDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : storeDialogError ? (
            <Alert severity="error">{storeDialogError?.message || 'Failed to load store details'}</Alert>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Company
                </Typography>
                <Typography variant="h6">
                  {storeDialogData?.company?.name || 'Company'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {storeDialogData?.company?.companyId || selectedStoreCompanyId || '-'}
                </Typography>
              </Box>

              {storeDialogStats ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={`${storeDialogStats.totalStores} ${storeDialogStats.totalStores === 1 ? 'Store' : 'Stores'}`}
                      size="small"
                      color="secondary"
                    />
                    {storeDialogStats.isMultiStore && (
                      <Chip label="Multi-Branch" size="small" color="info" variant="outlined" />
                    )}
                  </Box>

                  {storeDialogStats.totals && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Company-Wide Totals
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="primary.dark">
                              {storeDialogStats.totals.users || storeDialogData?.teamMembers?.totalCount || 0}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Users</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="primary">
                              {storeDialogStats.totals.products}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Products</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="info.main">
                              {storeDialogStats.totals.appointments}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Appointments</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="warning.main">
                              {storeDialogStats.totals.storageUsageGB?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Storage (GB)</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="info.main">
                              {storeDialogStats.totals.bandwidthUsageGB?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Bandwidth (GB)</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="secondary">
                              {storeDialogStats.totalStores}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Total Stores</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Hierarchy
                  </Typography>
                  {storeDialogStores.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No stores have been synced from the iOS app yet.
                    </Alert>
                  ) : (
                    <>
                      {storeDialogHeadquarters.length > 0 && (
                        <>
                          <Typography variant="caption" color="textSecondary">Headquarters</Typography>
                          <List dense sx={{ mb: 1 }}>
                            {storeDialogHeadquarters.map((store, idx) => (
                              <ListItem key={store.id || store.storeId || `hq-${idx}`} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <StoreIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={`${store.storeName || 'Headquarters'} (HQ)`}
                                  secondary={[
                                    store.location?.city,
                                    store.location?.province,
                                    store.location?.country,
                                  ].filter(Boolean).join(', ') || 'No location provided'}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                      {storeDialogBranches.length > 0 && (
                        <>
                          <Typography variant="caption" color="textSecondary">Branches</Typography>
                          <List dense sx={{ pl: 2 }}>
                            {storeDialogBranches.map((store, idx) => (
                              <ListItem key={store.id || store.storeId || `branch-${idx}`} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <AccountTreeIcon color="action" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={store.storeName || 'Branch'}
                                  secondary={[
                                    store.location?.city,
                                    store.location?.province,
                                    store.location?.country,
                                  ].filter(Boolean).join(', ') || 'No location provided'}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Store Details
                  </Typography>
                  {storeDialogStores.map((store, idx) => (
                    <Accordion key={store.id || store.storeId || idx} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <StoreIcon color={store.isHeadquarters ? 'primary' : 'action'} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {store.storeName}
                              {store.isHeadquarters && (
                                <Chip label="HQ" size="small" color="primary" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {store.storeCode || 'N/A'} • {store.storeType || 'Branch'}
                            </Typography>
                          </Box>
                          <Chip
                            label={store.status || 'UNKNOWN'}
                            size="small"
                            color={store.status === 'ACTIVE' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {/* Location Info */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                              <LocationOnIcon color="action" fontSize="small" />
                              <Box>
                                <Typography variant="body2">
                                  {[store.location?.street, store.location?.city, store.location?.province]
                                    .filter(Boolean).join(', ') || 'No address provided'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {[store.location?.postalCode, store.location?.country]
                                    .filter(Boolean).join(', ')}
                                </Typography>
                              </Box>
                            </Box>
                            {store.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PhoneIcon color="action" fontSize="small" />
                                <Typography variant="body2">{store.phone}</Typography>
                              </Box>
                            )}
                            {store.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon color="action" fontSize="small" />
                                <Typography variant="body2">{store.email}</Typography>
                              </Box>
                            )}
                          </Grid>
                          {/* Store Stats */}
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={1}>
                              <Grid item xs={6} sm={3}>
                                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'primary.light' }}>
                                  <PeopleIcon color="primary" sx={{ fontSize: 24 }} />
                                  <Typography variant="h6" color="primary.dark">{store.stats?.userCount || 0}</Typography>
                                  <Typography variant="caption">Users</Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100' }}>
                                  <InventoryIcon color="success" sx={{ fontSize: 24 }} />
                                  <Typography variant="h6">{store.stats?.productCount || 0}</Typography>
                                  <Typography variant="caption">Products</Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'info.light' }}>
                                  <CalendarMonthIcon color="info" sx={{ fontSize: 24 }} />
                                  <Typography variant="h6">{store.stats?.appointmentCount || 0}</Typography>
                                  <Typography variant="caption">Appts</Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.light' }}>
                                  <StorageIcon color="warning" sx={{ fontSize: 24 }} />
                                  <Typography variant="h6">
                                    {store.stats?.storageUsageGB?.toFixed(2) || '0.00'}
                                  </Typography>
                                  <Typography variant="caption">GB</Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              ) : (
                <Alert severity="info">Store data has not been synced for this company yet.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStoreDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Company Detail Dialog - Enhanced with Charts */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <BusinessIcon color="primary" />
          Company Details
          {detailData?.company?.isArchived && (
            <Chip
              icon={<ArchiveIcon />}
              label={`Archived ${detailData.company.archivedAt ? format(new Date(detailData.company.archivedAt), 'MMM d, yyyy') : ''}`}
              color="warning"
              size="small"
            />
          )}
          {detailData?.company?.isFraudSuspect && (
            <Chip
              label="Fraud Suspect"
              color="error"
              size="small"
            />
          )}
          {detailData?.company?.isOnlineNow && !detailData?.company?.isArchived && (
            <Chip
              icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
              label="Online"
              color="success"
              size="small"
              sx={{ ml: 'auto' }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : detailData?.company ? (
            <Box sx={{ pt: 2 }}>
              {/* Company Info Card */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Company Name</Typography>
                      <Typography variant="h6" gutterBottom>{detailData.company.name}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Account Number</Typography>
                      {detailData.company.accountNumber ? (
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main' }}>
                          {detailData.company.accountNumber}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                          Not assigned yet
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Company ID</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {detailData.company.companyId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Tooltip title="iCloud Account ID - identifies user across devices and reinstalls">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CloudIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" color="textSecondary">iCloud ID</Typography>
                        </Box>
                      </Tooltip>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', backgroundColor: 'action.hover', padding: '4px 8px', borderRadius: 1, display: 'inline-block' }}>
                        {detailData.company.cloudKitRecordID || detailData.company.archivedCloudKitRecordID || 'Not linked'}
                      </Typography>
                    </Grid>
                    {detailData.company.isArchived && (
                      <Grid item xs={12}>
                        <Alert severity="warning" icon={<ArchiveIcon />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            This company is ARCHIVED
                          </Typography>
                          <Typography variant="body2">
                            Archived on: {detailData.company.archivedAt ? format(new Date(detailData.company.archivedAt), 'MMMM d, yyyy \'at\' h:mm a') : 'Unknown'}
                          </Typography>
                          {detailData.company.archivedReason && (
                            <Typography variant="body2">
                              Reason: {detailData.company.archivedReason}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            Original iCloud ID: {detailData.company.archivedCloudKitRecordID || 'Unknown'}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Tooltip title="Used for service notifications and mass communications">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" color="textSecondary">Email Address</Typography>
                        </Box>
                      </Tooltip>
                      <Typography variant="body1" sx={{ fontWeight: detailData.company.email ? 'medium' : 'normal' }}>
                        {detailData.company.email || <Typography component="span" color="warning.main">Not provided</Typography>}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PublicIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="textSecondary">Country</Typography>
                      </Box>
                      <Typography variant="body1">{detailData.company.country || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Current Tier</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={detailData.company.currentTier}
                          color={tierColors[detailData.company.currentTier] || 'default'}
                        />
                        {!detailData.company.isArchived && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setChangeTierValue(detailData.company.currentTier || 'free');
                              setChangeTierReason('');
                              setChangeTierError(null);
                              setChangeTierOpen(true);
                            }}
                          >
                            Change Tier
                          </Button>
                        )}
                        {activeTierUpgradeLoading && (
                          <CircularProgress size={20} />
                        )}
                        {!detailData.company.isArchived && !activeTierUpgradeLoading && activeTierUpgradeData?.hasActiveUpgrade && activeTierUpgradeData.upgrade && (
                          <>
                            <Chip
                              size="small"
                              label={`Temporary: ${activeTierUpgradeData.upgrade.upgradedTier}`}
                              color="success"
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ width: '100%' }}>
                              Until {format(new Date(activeTierUpgradeData.upgrade.endDate), 'MMM d, yyyy')}
                              {' · '}
                              {formatDistanceToNow(new Date(activeTierUpgradeData.upgrade.endDate), { addSuffix: true })}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ExtensionIcon />}
                              onClick={() => {
                                setTierUpgradeExtendDays(7);
                                setTierUpgradeError(null);
                                setTierUpgradeExtendOpen(true);
                              }}
                            >
                              Extend
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => {
                                setTierUpgradeCancelReason('');
                                setTierUpgradeError(null);
                                setTierUpgradeCancelOpen(true);
                              }}
                            >
                              Cancel upgrade
                            </Button>
                          </>
                        )}
                        {!detailData.company.isArchived && !activeTierUpgradeLoading && !activeTierUpgradeData?.hasActiveUpgrade && canGrantTemporaryTierUpgrade(detailData.company.currentTier) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={<TrendingUpIcon />}
                            onClick={() => {
                              setTierUpgradeForm({ upgradedTier: 'professional', durationDays: 30, reason: '' });
                              setTierUpgradeError(null);
                              setTierUpgradeGrantOpen(true);
                            }}
                          >
                            Grant temporary upgrade
                          </Button>
                        )}
                      </Box>
                      {!detailData.company.isArchived && !activeTierUpgradeLoading && !activeTierUpgradeData?.hasActiveUpgrade && !canGrantTemporaryTierUpgrade(detailData.company.currentTier) && (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                          Temporary upgrades match the Tier Upgrades page: only Free and Professional companies can receive a timed upgrade to a higher tier.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Business Type</Typography>
                      <Typography variant="body1">{detailData.company.businessType || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Registered</Typography>
                      <Typography variant="body1">
                        {detailData.company.registrationDate
                          ? format(new Date(detailData.company.registrationDate), 'MMM d, yyyy')
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Last Active</Typography>
                      <Typography variant="body1">
                        {detailData.company.lastActiveDate
                          ? format(new Date(detailData.company.lastActiveDate), 'MMM d, yyyy HH:mm')
                          : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Company Limit Override */}
              <Accordion sx={{ mt: 3, border: detailData.company.maxCompaniesOverride ? '2px solid' : 'none', borderColor: 'warning.main' }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="warning" />
                    Company Limit Override
                    {detailData.company.maxCompaniesOverride && (
                      <Chip label="Active" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Company limit override is <strong>iCloud-account wide</strong>. Every company under the same
                    iCloud ID shows the same override (including companies you create for clients on your phone).
                    Grant / modify / remove applies to the whole account, not one company only.
                    This only changes how many companies the account may have. To bypass the
                    global Multi-Company kill switch in Features &amp; Limits, use{' '}
                    <strong>Multi-Company Feature Override</strong> below.
                  </Alert>

                  {overrideSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOverrideSuccess(null)}>
                      {overrideSuccess}
                    </Alert>
                  )}
                  {overrideError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOverrideError(null)}>
                      {overrideError}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Tier Default</Typography>
                      <Typography variant="body1">
                        {detailData.company.currentTier === 'free' ? '1 company' :
                         detailData.company.currentTier === 'enterprise' ? '10 companies' : '3 companies'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Effective Limit</Typography>
                      <Typography variant="h6" color={detailData.company.maxCompaniesOverride ? 'warning.main' : 'text.primary'}>
                        {detailData.company.maxCompaniesOverride === -1
                          ? '∞ Unlimited'
                          : `${detailData.company.maxCompaniesOverride ||
                              (detailData.company.currentTier === 'free' ? 1 :
                               detailData.company.currentTier === 'enterprise' ? 10 : 3)} companies`}
                      </Typography>
                    </Grid>
                    {detailData.company.maxCompaniesOverride && (
                      <>
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" color="textSecondary">Override By</Typography>
                          <Typography variant="body2">{detailData.company.overrideGrantedBy || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" color="textSecondary">Override Date</Typography>
                          <Typography variant="body2">
                            {detailData.company.overrideGrantedAt
                              ? format(new Date(detailData.company.overrideGrantedAt), 'MMM d, yyyy HH:mm')
                              : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                          <Typography variant="body2">{detailData.company.overrideReason || '-'}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => {
                        const currentOverride = detailData.company.maxCompaniesOverride;
                        const isCurrentlyUnlimited = currentOverride === -1;
                        setOverrideMaxCompanies(isCurrentlyUnlimited ? '' : (currentOverride?.toString() || ''));
                        setOverrideUnlimited(isCurrentlyUnlimited);
                        setOverrideReason(detailData.company.overrideReason || '');
                        setOverrideError(null);
                        setOverrideSuccess(null);
                        setOverrideModalOpen(true);
                      }}
                    >
                      {detailData.company.maxCompaniesOverride ? 'Modify Override' : 'Grant Override'}
                    </Button>
                    {detailData.company.maxCompaniesOverride && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={overrideLoading}
                        onClick={handleRemoveOverride}
                      >
                        {overrideLoading ? 'Removing...' : 'Remove Override'}
                      </Button>
                    )}
                  </Box>

                  {/* Override History */}
                  {detailData.company.overrideHistory?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Override History</Typography>
                      {detailData.company.overrideHistory.slice(-5).reverse().map((entry, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                          <Chip
                            label={entry.action}
                            size="small"
                            color={entry.action === 'grant' ? 'success' : entry.action === 'remove' ? 'error' : 'warning'}
                            sx={{ minWidth: 60 }}
                          />
                          <Typography variant="caption">
                            {entry.oldLimit === -1 ? '∞' : (entry.oldLimit ?? 'default')} → {entry.newLimit === -1 ? '∞' : (entry.newLimit ?? 'default')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            by {entry.performedBy} on {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm') : '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Multi-Company Feature Override (global kill-switch bypass) */}
              <Accordion sx={{ mt: 3, border: detailData.company.enableMultiCompanyOverride ? '2px solid' : 'none', borderColor: 'secondary.main' }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTreeIcon color="secondary" />
                    Multi-Company Feature Override
                    {detailData.company.enableMultiCompanyOverride && (
                      <Chip label="Active" size="small" color="secondary" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Grants this <strong>entire iCloud account</strong> permission to use multi-company UI
                    (company management, add company, switching) even when the global Multi-Company
                    kill switch is OFF in Configuration → Features &amp; Limits. Propagates to every
                    company under the same iCloud ID. Company count still follows the subscription
                    tier unless you also grant a Company Limit Override.
                  </Alert>

                  {multiCompanyOverrideSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMultiCompanyOverrideSuccess(null)}>
                      {multiCompanyOverrideSuccess}
                    </Alert>
                  )}
                  {multiCompanyOverrideError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMultiCompanyOverrideError(null)}>
                      {multiCompanyOverrideError}
                    </Alert>
                  )}

                  {detailData.company.enableMultiCompanyOverride ? (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">Granted By</Typography>
                        <Typography variant="body2">{detailData.company.multiCompanyOverrideGrantedBy || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">Granted At</Typography>
                        <Typography variant="body2">
                          {detailData.company.multiCompanyOverrideGrantedAt
                            ? format(new Date(detailData.company.multiCompanyOverrideGrantedAt), 'MMM d, yyyy HH:mm')
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                        <Typography variant="body2">{detailData.company.multiCompanyOverrideReason || '-'}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <TextField
                      label="Reason"
                      fullWidth
                      required
                      size="small"
                      value={multiCompanyOverrideReason}
                      onChange={(e) => setMultiCompanyOverrideReason(e.target.value)}
                      placeholder="Why should this account bypass the global multi-company kill switch?"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!detailData.company.enableMultiCompanyOverride ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={multiCompanyOverrideLoading || !multiCompanyOverrideReason.trim()}
                        onClick={async () => {
                          setMultiCompanyOverrideLoading(true);
                          setMultiCompanyOverrideError(null);
                          setMultiCompanyOverrideSuccess(null);
                          try {
                            const result = await companiesAPI.overrideMultiCompany({
                              companyId: detailData.company.companyId,
                              enabled: true,
                              reason: multiCompanyOverrideReason.trim(),
                            });
                            setMultiCompanyOverrideSuccess(
                              result.message +
                              (result.siblingsPropagated > 0 ? ` (propagated to ${result.siblingsPropagated} sibling companies)` : '')
                            );
                            setMultiCompanyOverrideReason('');
                            queryClient.invalidateQueries(['company-detail', selectedCompany]);
                          } catch (e) {
                            setMultiCompanyOverrideError(e?.message || 'Failed to grant override');
                          } finally {
                            setMultiCompanyOverrideLoading(false);
                          }
                        }}
                      >
                        {multiCompanyOverrideLoading ? 'Saving...' : 'Grant Override'}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={multiCompanyOverrideLoading}
                        onClick={async () => {
                          const confirmed = window.confirm(
                            'Remove the multi-company feature override for this entire iCloud account?\n\n' +
                            'The account will follow the global Multi-Company kill switch again.'
                          );
                          if (!confirmed) return;
                          setMultiCompanyOverrideLoading(true);
                          setMultiCompanyOverrideError(null);
                          setMultiCompanyOverrideSuccess(null);
                          try {
                            const result = await companiesAPI.overrideMultiCompany({
                              companyId: detailData.company.companyId,
                              action: 'remove',
                            });
                            setMultiCompanyOverrideSuccess(
                              result.message +
                              (result.siblingsPropagated > 0 ? ` (cleared ${result.siblingsPropagated} sibling companies)` : '')
                            );
                            queryClient.invalidateQueries(['company-detail', selectedCompany]);
                          } catch (e) {
                            setMultiCompanyOverrideError(e?.message || 'Failed to revoke override');
                          } finally {
                            setMultiCompanyOverrideLoading(false);
                          }
                        }}
                      >
                        {multiCompanyOverrideLoading ? 'Removing...' : 'Revoke Override'}
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Client Handoff Backup Capability */}
              <Accordion sx={{ mt: 3, border: detailData.company.allowClientHandoffBackup ? '2px solid' : 'none', borderColor: 'info.main' }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="info" />
                    Client Handoff Backup
                    {detailData.company.allowClientHandoffBackup && (
                      <Chip label="Enabled" size="small" color="info" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    When enabled, <strong>this entire iCloud account</strong> can use <strong>Client Handoff</strong> export
                    in the iOS Backup Manager (propagates to all companies under the same iCloud ID).
                    That export strips Enterprise / admin overrides and resets the plan to Free so the file is safe to give a client.
                    Grant it on any one of your companies — it is not lost when a client company is deleted later.
                  </Typography>

                  {handoffSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setHandoffSuccess(null)}>
                      {handoffSuccess}
                    </Alert>
                  )}
                  {handoffError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHandoffError(null)}>
                      {handoffError}
                    </Alert>
                  )}

                  {detailData.company.allowClientHandoffBackup ? (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">Granted By</Typography>
                        <Typography variant="body2">{detailData.company.clientHandoffGrantedBy || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">Granted At</Typography>
                        <Typography variant="body2">
                          {detailData.company.clientHandoffGrantedAt
                            ? format(new Date(detailData.company.clientHandoffGrantedAt), 'MMM d, yyyy HH:mm')
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                        <Typography variant="body2">{detailData.company.clientHandoffReason || '-'}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <TextField
                      label="Reason"
                      fullWidth
                      required
                      size="small"
                      value={handoffReason}
                      onChange={(e) => setHandoffReason(e.target.value)}
                      placeholder="Why is this account allowed to create client handoff backups?"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!detailData.company.allowClientHandoffBackup ? (
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        disabled={handoffLoading || !handoffReason.trim()}
                        onClick={async () => {
                          setHandoffLoading(true);
                          setHandoffError(null);
                          setHandoffSuccess(null);
                          try {
                            await companiesAPI.setClientHandoffCapability({
                              companyId: detailData.company.companyId,
                              enabled: true,
                              reason: handoffReason.trim(),
                            });
                            setHandoffSuccess('Client handoff backup capability granted');
                            setHandoffReason('');
                            queryClient.invalidateQueries(['company-detail', selectedCompany]);
                          } catch (e) {
                            setHandoffError(e?.message || 'Failed to grant capability');
                          } finally {
                            setHandoffLoading(false);
                          }
                        }}
                      >
                        {handoffLoading ? 'Saving...' : 'Grant Capability'}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={handoffLoading}
                        onClick={async () => {
                          setHandoffLoading(true);
                          setHandoffError(null);
                          setHandoffSuccess(null);
                          try {
                            await companiesAPI.setClientHandoffCapability({
                              companyId: detailData.company.companyId,
                              action: 'remove',
                            });
                            setHandoffSuccess('Client handoff backup capability revoked');
                            queryClient.invalidateQueries(['company-detail', selectedCompany]);
                          } catch (e) {
                            setHandoffError(e?.message || 'Failed to revoke capability');
                          } finally {
                            setHandoffLoading(false);
                          }
                        }}
                      >
                        {handoffLoading ? 'Removing...' : 'Revoke Capability'}
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Business-Type Override */}
              <Accordion sx={{ mt: 3, border: detailData.company.allowedBusinessTypesOverride?.length ? '2px solid' : 'none', borderColor: 'warning.main' }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExtensionIcon color="warning" />
                    Business-Type Override
                    {detailData.company.allowedBusinessTypesOverride?.length > 0 && (
                      <Chip label="Active" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Grant this account access to business types that are globally disabled in App Configuration.
                    The override is merged with the global enabled list on the iOS app.
                  </Typography>

                  {btOverrideSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBtOverrideSuccess(null)}>
                      {btOverrideSuccess}
                    </Alert>
                  )}
                  {btOverrideError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBtOverrideError(null)}>
                      {btOverrideError}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Active Override</Typography>
                      {detailData.company.allowedBusinessTypesOverride?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {detailData.company.allowedBusinessTypesOverride.map((type) => {
                            const entry = BUSINESS_TYPE_CATALOG.find(c => c.key === type);
                            return (
                              <Chip
                                key={type}
                                label={entry ? entry.label : type}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2">None — using global enabled list only</Typography>
                      )}
                    </Grid>
                    {detailData.company.allowedBusinessTypesOverride?.length > 0 && (
                      <>
                        <Grid item xs={6} md={4}>
                          <Typography variant="subtitle2" color="textSecondary">Granted By</Typography>
                          <Typography variant="body2">{detailData.company.businessTypesOverrideGrantedBy || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="subtitle2" color="textSecondary">Granted At</Typography>
                          <Typography variant="body2">
                            {detailData.company.businessTypesOverrideGrantedAt
                              ? format(new Date(detailData.company.businessTypesOverrideGrantedAt), 'MMM d, yyyy HH:mm')
                              : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                          <Typography variant="body2">{detailData.company.businessTypesOverrideReason || '-'}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => {
                        setBtOverrideSelected(detailData.company.allowedBusinessTypesOverride || []);
                        setBtOverrideReason(detailData.company.businessTypesOverrideReason || '');
                        setBtOverrideError(null);
                        setBtOverrideSuccess(null);
                        setBtOverrideModalOpen(true);
                      }}
                    >
                      {detailData.company.allowedBusinessTypesOverride?.length ? 'Modify Override' : 'Grant Override'}
                    </Button>
                    {detailData.company.allowedBusinessTypesOverride?.length > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={btOverrideLoading}
                        onClick={handleRemoveBusinessTypesOverride}
                      >
                        {btOverrideLoading ? 'Removing...' : 'Remove Override'}
                      </Button>
                    )}
                  </Box>

                  {detailData.company.businessTypesOverrideHistory?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Override History</Typography>
                      {detailData.company.businessTypesOverrideHistory.slice(-5).reverse().map((entry, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={entry.action}
                            size="small"
                            color={entry.action === 'grant' ? 'success' : entry.action === 'remove' ? 'error' : 'warning'}
                            sx={{ minWidth: 60 }}
                          />
                          <Typography variant="caption">
                            [{(entry.oldTypes || []).join(', ') || '∅'}] → [{(entry.newTypes || []).join(', ') || '∅'}]
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            by {entry.performedBy} on {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm') : '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Novo hosted AI — account override (PostgreSQL) */}
              <Accordion sx={{ mt: 3 }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExtensionIcon color="primary" />
                    Novo hosted AI — account override (PostgreSQL)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Writes to <code>Account.hostedAIOverride</code> for the account linked to this company.
                    Leave fields empty to inherit tier defaults. Saving with no fields set clears the override.
                    Operation-level costs use the JSON object field below.
                  </Typography>
                  {hostedAIOvrMsg && (
                    <Alert severity={hostedAIOvrMsg.type || 'info'} sx={{ mb: 2 }} onClose={() => setHostedAIOvrMsg(null)}>
                      {hostedAIOvrMsg.text}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="hosted-ai-enabled-label">Hosted AI enabled</InputLabel>
                        <Select
                          labelId="hosted-ai-enabled-label"
                          label="Hosted AI enabled"
                          value={hostedAIOvrForm.hostedAIEnabled}
                          onChange={(e) =>
                            setHostedAIOvrForm((f) => ({ ...f, hostedAIEnabled: e.target.value }))
                          }
                        >
                          <MenuItem value={HOSTED_AI_OVR_INHERIT}>Inherit</MenuItem>
                          <MenuItem value="true">On</MenuItem>
                          <MenuItem value="false">Off</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Monthly prompt limit override"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.monthlyPromptLimitOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, monthlyPromptLimitOverride: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Daily prompt limit override"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.dailyPromptLimitOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, dailyPromptLimitOverride: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="hosted-ai-bulk-en-label">Bulk operations</InputLabel>
                        <Select
                          labelId="hosted-ai-bulk-en-label"
                          label="Bulk operations"
                          value={hostedAIOvrForm.bulkOperationsEnabledOverride}
                          onChange={(e) =>
                            setHostedAIOvrForm((f) => ({
                              ...f,
                              bulkOperationsEnabledOverride: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value={HOSTED_AI_OVR_INHERIT}>Inherit</MenuItem>
                          <MenuItem value="true">Force on</MenuItem>
                          <MenuItem value="false">Force off</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max items per bulk job"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.maxItemsPerBulkJobOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, maxItemsPerBulkJobOverride: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max bulk jobs per day"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.maxBulkJobsPerDayOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, maxBulkJobsPerDayOverride: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Requests per minute (company)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.requestsPerMinuteOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, requestsPerMinuteOverride: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Requests per minute (per user)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={hostedAIOvrForm.perUserRequestsPerMinuteOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({
                            ...f,
                            perUserRequestsPerMinuteOverride: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max concurrent hosted requests"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={hostedAIOvrForm.maxConcurrentRequestsOverride}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({
                            ...f,
                            maxConcurrentRequestsOverride: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="byok-prefer-ovr-label">BYOK: prefer when configured</InputLabel>
                        <Select
                          labelId="byok-prefer-ovr-label"
                          label="BYOK: prefer when configured"
                          value={hostedAIOvrForm.byokPrefer}
                          onChange={(e) =>
                            setHostedAIOvrForm((f) => ({ ...f, byokPrefer: e.target.value }))
                          }
                        >
                          <MenuItem value={HOSTED_AI_OVR_INHERIT}>Inherit (BI global)</MenuItem>
                          <MenuItem value="true">Force prefer BYOK</MenuItem>
                          <MenuItem value="false">Force prefer hosted</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="byok-fb-ovr-label">BYOK: auto-fallback when hosted exhausted</InputLabel>
                        <Select
                          labelId="byok-fb-ovr-label"
                          label="BYOK: auto-fallback when hosted exhausted"
                          value={hostedAIOvrForm.byokAutoFallback}
                          onChange={(e) =>
                            setHostedAIOvrForm((f) => ({ ...f, byokAutoFallback: e.target.value }))
                          }
                        >
                          <MenuItem value={HOSTED_AI_OVR_INHERIT}>Inherit (BI global)</MenuItem>
                          <MenuItem value="true">Enable auto-fallback</MenuItem>
                          <MenuItem value="false">Disable auto-fallback</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Override notes (stored on account)"
                        multiline
                        minRows={2}
                        value={hostedAIOvrForm.notes}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, notes: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Operation cost overrides (JSON object, optional)"
                        value={hostedAIOvrForm.operationCostOverridesText}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({
                            ...f,
                            operationCostOverridesText: e.target.value,
                          }))
                        }
                        placeholder='{ "inventory_caption": { "creditWeight": 2 } }'
                        sx={{ fontFamily: 'monospace' }}
                        helperText="Keys are operation types from the hosted AI registry."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Audit note (BI log only)"
                        multiline
                        minRows={2}
                        value={hostedAIOvrForm.auditNote}
                        onChange={(e) =>
                          setHostedAIOvrForm((f) => ({ ...f, auditNote: e.target.value }))
                        }
                        helperText="Recorded with the sync action; not shown to end users."
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      disabled={hostedAIOvrBusy || !selectedCompany}
                      onClick={async () => {
                        try {
                          setHostedAIOvrBusy(true);
                          const payload = buildHostedAIOverridePayload(hostedAIOvrForm);
                          const prev = detailData?.postgresHostedAIOverride;
                          const merged = {
                            ...stripManagedHostedAIKeys(prev),
                            ...payload.hostedAIOverride,
                          };
                          const toSave = Object.keys(merged).length === 0 ? null : merged;
                          await companiesAPI.saveHostedAIAccountOverride(selectedCompany, {
                            hostedAIOverride: toSave,
                            auditNote: payload.auditNote,
                          });
                          setHostedAIOvrMsg({ type: 'success', text: 'Saved to website database.' });
                          queryClient.invalidateQueries(['company-detail', selectedCompany]);
                        } catch (e) {
                          setHostedAIOvrMsg({ type: 'error', text: e?.message || 'Save failed' });
                        } finally {
                          setHostedAIOvrBusy(false);
                        }
                      }}
                    >
                      Save override
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      disabled={hostedAIOvrBusy || !selectedCompany}
                      onClick={async () => {
                        try {
                          setHostedAIOvrBusy(true);
                          await companiesAPI.saveHostedAIAccountOverride(selectedCompany, {
                            hostedAIOverride: null,
                            auditNote: hostedAIOvrForm.auditNote?.trim() || undefined,
                          });
                          setHostedAIOvrForm(postgresOverrideToHostedAIForm(null));
                          setHostedAIOvrMsg({ type: 'success', text: 'Override cleared on website database.' });
                          queryClient.invalidateQueries(['company-detail', selectedCompany]);
                        } catch (e) {
                          setHostedAIOvrMsg({ type: 'error', text: e?.message || 'Clear failed' });
                        } finally {
                          setHostedAIOvrBusy(false);
                        }
                      }}
                    >
                      Clear override
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Company Website */}
              {(() => {
                const slug = detailData.company.companySlug;
                const customDomain = detailData.company.customDomain;
                const websiteURL = detailData.company.websiteURL;
                const primaryHref = customDomain
                  ? (customDomain.startsWith('http') ? customDomain : `https://${customDomain}`)
                  : slug
                    ? `https://${slug}.novosolution.org`
                    : (websiteURL
                      ? (websiteURL.startsWith('http') ? websiteURL : `https://${websiteURL}`)
                      : null);
                const primaryLabel = customDomain
                  || (slug ? `${slug}.novosolution.org` : null)
                  || websiteURL
                  || null;
                if (!primaryHref && !slug && !websiteURL) return null;
                return (
                  <Card sx={{ mb: 3, mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageIcon color="primary" />
                        Company Website
                      </Typography>
                      <Grid container spacing={2}>
                        {slug && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="textSecondary">Subdomain Slug</Typography>
                            <Chip label={slug} color="primary" variant="outlined" />
                          </Grid>
                        )}
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="textSecondary">Website URL</Typography>
                          {primaryHref ? (
                            <Typography variant="body1">
                              <a
                                href={primaryHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline' }}
                              >
                                {primaryLabel}
                              </a>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">Not configured</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                          <Chip
                            label={detailData.company.websiteEnabled === false || detailData.company.isArchived ? 'Inactive' : 'Active'}
                            size="small"
                            color={detailData.company.websiteEnabled === false || detailData.company.isArchived ? 'error' : 'success'}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Team & Devices Summary Card */}
              <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }}>
                <CardContent>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          borderRadius: 2,
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" color="white">
                            {detailData.teamMembers?.totalCount || detailData.company.usageMetrics?.teamMembersCount || 0}
                          </Typography>
                          <Typography variant="body1" color="rgba(255,255,255,0.8)">
                            Team Members
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          borderRadius: 2,
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <DevicesIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" color="white">
                            {detailData.company.connectedDevices?.length || detailData.company.deviceCount || 0}
                          </Typography>
                          <Typography variant="body1" color="rgba(255,255,255,0.8)">
                            Connected Devices
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 1,
                        py: 1,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                      }}>
                        <Typography variant="h6" color="white" sx={{ fontWeight: 'medium' }}>
                          {detailData.teamMembers?.totalCount || detailData.company.usageMetrics?.teamMembersCount || 0} team member{(detailData.teamMembers?.totalCount || detailData.company.usageMetrics?.teamMembersCount || 0) !== 1 ? 's' : ''} across {detailData.company.connectedDevices?.length || detailData.company.deviceCount || 0} device{(detailData.company.connectedDevices?.length || detailData.company.deviceCount || 0) !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Team Members Card (from iOS App) */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon color="primary" />
                    Team Members (iOS App)
                    {detailData.teamMembers?.lastUpdated && (
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                        Last synced: {format(new Date(detailData.teamMembers.lastUpdated), 'MMM d, HH:mm')}
                      </Typography>
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                        <PeopleIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold" color="primary.dark">
                          {detailData.teamMembers?.totalCount || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total Team Members
                        </Typography>
                      </Paper>
                    </Grid>
                    {/* Per-Store User Breakdown */}
                    {detailData.teamMembers?.perStoreBreakdown?.length > 0 && (
                      detailData.teamMembers.perStoreBreakdown.map((store, idx) => (
                        <Grid item xs={6} sm={4} md={3} key={store.storeId || idx}>
                          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                            <StoreIcon color="action" sx={{ fontSize: 24, mb: 0.5 }} />
                            <Typography variant="h5" fontWeight="bold">
                              {store.userCount || 0}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {store.storeName || 'Unknown Store'}
                            </Typography>
                            {store.storeCode && store.storeCode !== 'N/A' && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                ({store.storeCode})
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))
                    )}
                  </Grid>
                  {(!detailData.teamMembers || detailData.teamMembers.totalCount === 0) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Team member data will appear after the iOS app sends its next heartbeat.
                    </Alert>
                  )}

                  {/* Team Member Details */}
                  {detailData.teamMembers?.teamMemberDetails?.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" />
                        Team Member Details
                      </Typography>
                      {isMobile ? (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {detailData.teamMembers.teamMemberDetails.map((member, idx) => (
                            <Paper key={member.username || idx} sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {member.isCompanyAdmin && <AdminPanelSettingsIcon color="warning" fontSize="small" />}
                                <Typography variant="body2" fontWeight={600}>{member.username}</Typography>
                                <Chip
                                  label={member.isActive ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={member.isActive ? 'success' : 'default'}
                                  sx={{ ml: 'auto' }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={member.role}
                                  size="small"
                                  color={
                                    member.role === 'Admin' ? 'error' :
                                    member.role === 'Branch Manager' ? 'warning' :
                                    member.role === 'Sales Representative' ? 'success' :
                                    'default'
                                  }
                                  variant="outlined"
                                />
                              </Box>
                              {member.assignedStores?.length > 0 && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                  Stores: {member.assignedStores.join(', ')}
                                </Typography>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                      <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Username</strong></TableCell>
                              <TableCell><strong>Role</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Assigned Stores</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detailData.teamMembers.teamMemberDetails.map((member, idx) => (
                              <TableRow key={member.username || idx}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {member.isCompanyAdmin && (
                                      <Tooltip title="Company Admin">
                                        <AdminPanelSettingsIcon color="warning" fontSize="small" />
                                      </Tooltip>
                                    )}
                                    {member.username}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={member.role}
                                    size="small"
                                    color={
                                      member.role === 'Admin' ? 'error' :
                                      member.role === 'Branch Manager' ? 'warning' :
                                      member.role === 'Sales Representative' ? 'success' :
                                      'default'
                                    }
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={member.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={member.isActive ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {member.assignedStores?.length > 0
                                    ? member.assignedStores.join(', ')
                                    : '-'
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Connected Devices Card */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DevicesIcon color="primary" />
                    Connected Devices
                    <Chip
                      label={detailData.company.connectedDevices?.length || detailData.company.deviceCount || 0}
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  {detailData.company.connectedDevices?.length > 0 ? (
                    isMobile ? (
                      <Grid container spacing={1.5} sx={{ mt: 1 }}>
                        {detailData.company.connectedDevices.map((device, idx) => (
                          <Grid item xs={12} key={device.deviceId || idx}>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DevicesIcon fontSize="small" color="action" />
                                <Typography variant="body2" fontWeight={600}>{device.deviceModel || 'Unknown Device'}</Typography>
                                {device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000) && (
                                  <CircleIcon sx={{ fontSize: 8, color: 'success.main', ml: 'auto' }} />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Chip label={`iOS ${device.osVersion || '?'}`} size="small" variant="outlined" />
                                <Chip label={`v${device.appVersion || '?'}`} size="small" variant="outlined" />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="textSecondary">
                                  First: {device.firstActiveAt ? format(new Date(device.firstActiveAt), 'MMM d, yyyy') : '-'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Last: {device.lastSeen ? format(new Date(device.lastSeen), 'MMM d, HH:mm') : '-'}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Device Model</TableCell>
                            <TableCell>iOS Version</TableCell>
                            <TableCell>App Version</TableCell>
                            <TableCell>First Active</TableCell>
                            <TableCell>Last Seen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailData.company.connectedDevices.map((device, idx) => (
                            <TableRow key={device.deviceId || idx}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DevicesIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {device.deviceModel || 'Unknown Device'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={`iOS ${device.osVersion || '?'}`} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">
                                  v{device.appVersion || '?'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">
                                  {device.firstActiveAt
                                    ? format(new Date(device.firstActiveAt), 'MMM d, yyyy')
                                    : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000) && (
                                    <CircleIcon sx={{ fontSize: 8, color: 'success.main' }} />
                                  )}
                                  <Typography variant="body2">
                                    {device.lastSeen
                                      ? format(new Date(device.lastSeen), 'MMM d, HH:mm')
                                      : '-'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    )
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Device information will appear after the iOS app sends its next heartbeat.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* ── Fraud Management Actions Card ─────────────────────────────────── */}
              {(detailData?.company?.isFraudSuspect || detailData?.company?.isBanned || detailData?.company?.subscriptionStatus === 'suspended') && (
                <Card sx={{ mb: 3, borderLeft: '4px solid #f44336', bgcolor: 'rgba(244,67,54,0.04)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                      <SecurityIcon />
                      Fraud Management Actions
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {detailData.company.isBanned && (
                        <Chip icon={<BlockIcon />} label="Banned" color="error" size="small" />
                      )}
                      {detailData.company.subscriptionStatus === 'suspended' && (
                        <Chip label="Suspended" color="warning" size="small" />
                      )}
                      {detailData.company.isFraudSuspect && (
                        <Chip icon={<FlagIcon />} label="Fraud Suspect" color="error" size="small" />
                      )}
                      {(detailData.company.startFreshCount || 0) > 0 && (
                        <Chip label={`Start Fresh × ${detailData.company.startFreshCount}`} size="small" color="warning" />
                      )}
                    </Box>
                    {fraudActionError && <Alert severity="error" sx={{ mb: 1 }}>{fraudActionError}</Alert>}
                    {fraudActionSuccess && <Alert severity="success" sx={{ mb: 1 }}>{fraudActionSuccess}</Alert>}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {detailData.company.isFraudSuspect && (
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => {
                            setResolveFraudNotes('');
                            setResolveFraudOpen(true);
                          }}
                        >
                          Resolve Fraud Flag
                        </Button>
                      )}
                      {detailData.company.currentTier !== 'free' && (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          startIcon={<ArrowDownwardIcon />}
                          onClick={() => {
                            setForceDowngradeReason('');
                            setForceDowngradeOpen(true);
                          }}
                        >
                          Force Downgrade to Free
                        </Button>
                      )}
                      {detailData.company.subscriptionStatus !== 'suspended' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<BlockIcon />}
                          onClick={() => {
                            setSuspendReason('');
                            setSuspendOpen(true);
                          }}
                        >
                          Suspend Account
                        </Button>
                      )}
                      {detailData.company.subscriptionStatus === 'suspended' && (
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => {
                            setUnsuspendNotes('');
                            setUnsuspendOpen(true);
                          }}
                        >
                          Lift Suspension
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<PersonOffIcon />}
                        sx={{ borderColor: 'grey.500', color: 'grey.700' }}
                        onClick={() => {
                          setBanReason('');
                          setBanOpen(true);
                        }}
                        disabled={detailData.company.isBanned}
                      >
                        {detailData.company.isBanned ? 'User Banned' : 'Permanently Ban iCloud User'}
                      </Button>
                      {detailData.company.isBanned && (
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => {
                            setUnbanReason('');
                            setUnbanOpen(true);
                          }}
                        >
                          Lift Ban
                        </Button>
                      )}
                      {detailData.company.cloudKitRecordID && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CloudIcon />}
                          onClick={() => openIcloudCompaniesDialog(detailData.company.cloudKitRecordID)}
                        >
                          View iCloud History
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Website Statistics Card */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon color="primary" />
                    Website Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <InventoryIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h5" fontWeight="bold">
                          {detailData.websiteStats?.productCount || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Products
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <StorageIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h5" fontWeight="bold">
                          {detailData.websiteStats?.storageUsageGB?.toFixed(2) || '0.00'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Storage (GB)
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <SpeedIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h5" fontWeight="bold">
                          {detailData.websiteStats?.bandwidthUsageGB?.toFixed(2) || '0.00'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Bandwidth (GB)
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <CalendarMonthIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h5" fontWeight="bold">
                          {detailData.websiteStats?.totalAppointments || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Total Appointments
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <EventRepeatIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="h5" fontWeight="bold">
                          {detailData.websiteStats?.monthlyAppointments || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          This Month
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Store/Branch Statistics (available for all tiers when stores exist) */}
              {detailData.storeStats && (
                <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StoreIcon color="secondary" />
                      Store/Branch Statistics
                      <Chip
                        label={`${detailData.storeStats.totalStores} ${detailData.storeStats.totalStores === 1 ? 'Store' : 'Stores'}`}
                        size="small"
                        color="secondary"
                        sx={{ ml: 1 }}
                      />
                      {detailData.storeStats.isMultiStore && (
                        <Chip label="Multi-Store" size="small" color="info" variant="outlined" />
                      )}
                    </Typography>

                    {/* Store Totals Summary */}
                    {detailData.storeStats.totals && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Company-Wide Totals
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="primary.dark">
                                {detailData.storeStats.totals.users || detailData.teamMembers?.totalCount || 0}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Users</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="primary">
                                {detailData.storeStats.totals.products}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Products</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="info.main">
                                {detailData.storeStats.totals.appointments}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Appointments</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="warning.main">
                                {detailData.storeStats.totals.storageUsageGB?.toFixed(2) || '0.00'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Storage (GB)</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="info.main">
                                {detailData.storeStats.totals.bandwidthUsageGB?.toFixed(2) || '0.00'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Bandwidth (GB)</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="secondary">
                                {detailData.storeStats.totalStores}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">Total Stores</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Individual Store Details */}
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Per-Store Breakdown
                    </Typography>
                    {detailData.storeStats.stores.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No stores have been synced from the iOS app yet.
                        To see store statistics, the company needs to sync their stores from the iOS app
                        using the Website Integration settings.
                      </Alert>
                    ) : null}
                    {detailData.storeStats.stores.map((store, idx) => (
                      <Accordion key={store.id || idx} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <StoreIcon color={store.isHeadquarters ? 'primary' : 'action'} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {store.storeName}
                                {store.isHeadquarters && (
                                  <Chip label="HQ" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {store.storeCode} • {store.storeType}
                              </Typography>
                            </Box>
                            <Chip
                              label={store.status}
                              size="small"
                              color={store.status === 'ACTIVE' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            {/* Location Info */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                <LocationOnIcon color="action" fontSize="small" />
                                <Box>
                                  <Typography variant="body2">
                                    {[store.location?.street, store.location?.city, store.location?.province]
                                      .filter(Boolean).join(', ') || 'No address provided'}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {[store.location?.postalCode, store.location?.country]
                                      .filter(Boolean).join(', ')}
                                  </Typography>
                                </Box>
                              </Box>
                              {store.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <PhoneIcon color="action" fontSize="small" />
                                  <Typography variant="body2">{store.phone}</Typography>
                                </Box>
                              )}
                              {store.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EmailIcon color="action" fontSize="small" />
                                  <Typography variant="body2">{store.email}</Typography>
                                </Box>
                              )}
                            </Grid>
                            {/* Store Stats */}
                            <Grid item xs={12} md={6}>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'primary.light' }}>
                                    <PeopleIcon color="primary" sx={{ fontSize: 24 }} />
                                    <Typography variant="h6" color="primary.dark">{store.stats?.userCount || 0}</Typography>
                                    <Typography variant="caption">Users</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100' }}>
                                    <InventoryIcon color="success" sx={{ fontSize: 24 }} />
                                    <Typography variant="h6">{store.stats?.productCount || 0}</Typography>
                                    <Typography variant="caption">Products</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100' }}>
                                    <CalendarMonthIcon color="info" sx={{ fontSize: 24 }} />
                                    <Typography variant="h6">{store.stats?.appointmentCount || 0}</Typography>
                                    <Typography variant="caption">Appts</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100' }}>
                                    <StorageIcon color="warning" sx={{ fontSize: 24 }} />
                                    <Typography variant="h6">
                                      {store.stats?.storageUsageGB?.toFixed(2) || '0.00'}
                                    </Typography>
                                    <Typography variant="caption">GB</Typography>
                                  </Paper>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* AI Usage Chart & Current Month */}
              <Grid container spacing={3}>
                {/* Usage History Chart */}
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>AI Usage History (Last 6 Months)</Typography>
                      {detailData.usageHistory?.length > 0 ? (
                        <Box sx={{ height: 250 }}>
                          <Line
                            data={{
                              labels: detailData.usageHistory.map(h => h.label),
                              datasets: [
                                {
                                  label: 'Text',
                                  data: detailData.usageHistory.map(h => h.text),
                                  borderColor: chartColors.primary,
                                  backgroundColor: chartColors.primaryLight,
                                  tension: 0.3,
                                },
                                {
                                  label: 'Image',
                                  data: detailData.usageHistory.map(h => h.image),
                                  borderColor: chartColors.purple,
                                  backgroundColor: chartColors.purpleLight,
                                  tension: 0.3,
                                },
                                {
                                  label: 'Total',
                                  data: detailData.usageHistory.map(h => h.total),
                                  borderColor: chartColors.success,
                                  backgroundColor: chartColors.successLight,
                                  borderWidth: 2,
                                  borderDash: [5, 5],
                                  tension: 0.3,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'bottom' },
                              },
                              scales: {
                                y: { beginAtZero: true },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Alert severity="info">No usage history available</Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Current Month Usage */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>This Month</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">Text Generations</Typography>
                          <Typography variant="h6">{detailData.aiUsage?.text || 0}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">Image Generations</Typography>
                          <Typography variant="h6">{detailData.aiUsage?.image || 0}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">Video Generations</Typography>
                          <Typography variant="h6">{detailData.aiUsage?.video || 0}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">Voice Generations</Typography>
                          <Typography variant="h6">{detailData.aiUsage?.voice || 0}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary">Image Processing</Typography>
                          <Typography variant="h6">{detailData.aiUsage?.imageProcessing || 0}</Typography>
                        </Box>
                        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5, mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" fontWeight="bold">Total</Typography>
                          <Typography variant="h5" color="primary">
                            {(detailData.aiUsage?.text || 0) + (detailData.aiUsage?.image || 0) +
                             (detailData.aiUsage?.video || 0) + (detailData.aiUsage?.voice || 0) +
                             (detailData.aiUsage?.imageProcessing || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Connected Devices */}
              {detailData.company.connectedDevices?.length > 0 && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DevicesIcon color="action" />
                      Connected Devices ({detailData.company.connectedDevices.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {detailData.company.connectedDevices.map((device, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={device.deviceId || idx}>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="body2" fontWeight="bold">{device.deviceModel || 'Unknown Device'}</Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              iOS {device.osVersion} • v{device.appVersion}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Last seen: {device.lastSeen ? format(new Date(device.lastSeen), 'MMM d, HH:mm') : '-'}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Tier History Timeline */}
              {detailData.company.tierHistory?.length > 0 && (
                <Accordion sx={{ mt: 3 }} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon color="action" />
                      Tier History
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ position: 'relative', pl: 3 }}>
                      {/* Timeline line */}
                      <Box sx={{
                        position: 'absolute',
                        left: 8,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        bgcolor: 'grey.300'
                      }} />

                      {detailData.company.tierHistory.map((entry, idx) => (
                        <Box key={idx} sx={{ position: 'relative', mb: 2, pb: 2 }}>
                          {/* Timeline dot */}
                          <Box sx={{
                            position: 'absolute',
                            left: -25,
                            top: 4,
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: tierColors[entry.tier] === 'default' ? 'grey.400' :
                                    tierColors[entry.tier] === 'primary' ? 'primary.main' : 'secondary.main',
                            border: 2,
                            borderColor: 'white',
                            boxShadow: 1,
                          }} />

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={entry.tier} size="small" color={tierColors[entry.tier]} />
                              <Typography variant="caption" color="textSecondary">
                                {entry.reason}
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              {format(new Date(entry.startDate), 'MMM d, yyyy')}
                              {entry.endDate ? ` → ${format(new Date(entry.endDate), 'MMM d, yyyy')}` : ' → Present'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          ) : (
            <Alert severity="error">Company not found</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            color="error"
            variant="outlined"
            onClick={() => {
              const detail = detailData?.company;
              if (detail) {
                handleDeleteCompany(
                  detail.companyId,
                  detail.name,
                  detail.currentTier,
                  detail.totalRevenue,
                  detail.paymentHistory?.length || 0,
                  detail.subscriptionStatus
                );
              }
            }}
            disabled={!detailData?.company}
          >
            Remove Company
          </Button>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Temporary tier upgrade (same API as Tier Upgrades page) */}
      <Dialog
        open={tierUpgradeGrantOpen}
        onClose={() => {
          if (!createTierUpgradeMutation.isLoading) {
            setTierUpgradeGrantOpen(false);
            setTierUpgradeError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Grant temporary tier upgrade</DialogTitle>
        <DialogContent>
          {tierUpgradeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTierUpgradeError(null)}>
              {tierUpgradeError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">Company</Typography>
              <Typography variant="body1">{detailData?.company?.name || '—'}</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                {detailData?.company?.companyId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Upgrade to</InputLabel>
                <Select
                  value={tierUpgradeForm.upgradedTier}
                  label="Upgrade to"
                  onChange={(e) => setTierUpgradeForm((f) => ({ ...f, upgradedTier: e.target.value }))}
                >
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Duration (days)"
                value={tierUpgradeForm.durationDays}
                onChange={(e) => setTierUpgradeForm((f) => ({ ...f, durationDays: parseInt(e.target.value, 10) || 1 }))}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason"
                value={tierUpgradeForm.reason}
                onChange={(e) => setTierUpgradeForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this upgrade being granted?"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTierUpgradeGrantOpen(false);
              setTierUpgradeError(null);
            }}
            disabled={createTierUpgradeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              createTierUpgradeMutation.isLoading
              || !detailData?.company?.companyId
              || !tierUpgradeForm.reason?.trim()
            }
            onClick={() => {
              setTierUpgradeError(null);
              createTierUpgradeMutation.mutate({
                companyId: detailData.company.companyId,
                upgradedTier: tierUpgradeForm.upgradedTier,
                durationDays: tierUpgradeForm.durationDays,
                reason: tierUpgradeForm.reason.trim(),
              });
            }}
          >
            {createTierUpgradeMutation.isLoading ? <CircularProgress size={24} /> : 'Grant upgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tierUpgradeCancelOpen}
        onClose={() => {
          if (!cancelTierUpgradeMutation.isLoading) {
            setTierUpgradeCancelOpen(false);
            setTierUpgradeError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel tier upgrade</DialogTitle>
        <DialogContent>
          {tierUpgradeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTierUpgradeError(null)}>
              {tierUpgradeError}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }}>
            Cancel the active temporary upgrade for {detailData?.company?.name || 'this company'}? Their tier will revert per portal rules.
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={tierUpgradeCancelReason}
            onChange={(e) => setTierUpgradeCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTierUpgradeCancelOpen(false);
              setTierUpgradeError(null);
            }}
            disabled={cancelTierUpgradeMutation.isLoading}
          >
            Back
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelTierUpgradeMutation.isLoading || !activeTierUpgradeData?.upgrade?._id}
            onClick={() => {
              setTierUpgradeError(null);
              cancelTierUpgradeMutation.mutate({
                id: activeTierUpgradeData.upgrade._id,
                reason: tierUpgradeCancelReason,
              });
            }}
          >
            {cancelTierUpgradeMutation.isLoading ? <CircularProgress size={24} /> : 'Cancel upgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tierUpgradeExtendOpen}
        onClose={() => {
          if (!extendTierUpgradeMutation.isLoading) {
            setTierUpgradeExtendOpen(false);
            setTierUpgradeError(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend tier upgrade</DialogTitle>
        <DialogContent>
          {tierUpgradeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTierUpgradeError(null)}>
              {tierUpgradeError}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }}>
            Add days to the active upgrade for {detailData?.company?.name || 'this company'}.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Additional days"
            value={tierUpgradeExtendDays}
            onChange={(e) => setTierUpgradeExtendDays(parseInt(e.target.value, 10) || 1)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTierUpgradeExtendOpen(false);
              setTierUpgradeError(null);
            }}
            disabled={extendTierUpgradeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={extendTierUpgradeMutation.isLoading || !activeTierUpgradeData?.upgrade?._id}
            onClick={() => {
              setTierUpgradeError(null);
              extendTierUpgradeMutation.mutate({
                id: activeTierUpgradeData.upgrade._id,
                additionalDays: tierUpgradeExtendDays,
              });
            }}
          >
            {extendTierUpgradeMutation.isLoading ? <CircularProgress size={24} /> : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Company Confirmation Dialog — multi-step with paying customer guard */}
      <Dialog open={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); setDeletePayingError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          Permanently Remove Company
          {companyToDelete?.tier && (
            <Chip
              size="small"
              label={companyToDelete.tier.toUpperCase()}
              color={companyToDelete.tier === 'free' ? 'default' : companyToDelete.tier === 'enterprise' ? 'error' : companyToDelete.tier === 'professional' ? 'primary' : 'warning'}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {companyToDelete && (() => {
            const isPaid = ['standard', 'professional', 'enterprise'].includes(companyToDelete.tier);
            const hasPay = (companyToDelete.paymentCount || 0) > 0 || (companyToDelete.totalRevenue || 0) > 0;
            const isPayingCustomer = isPaid || hasPay;

            return (
              <Box>
                {/* Company info summary */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                  <Typography variant="h6" gutterBottom>{companyToDelete.companyName}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2"><strong>Tier:</strong> {companyToDelete.tier?.toUpperCase() || 'FREE'}</Typography>
                    {companyToDelete.subscriptionStatus && (
                      <Typography variant="body2"><strong>Status:</strong> {companyToDelete.subscriptionStatus}</Typography>
                    )}
                    {(companyToDelete.totalRevenue || 0) > 0 && (
                      <Typography variant="body2"><strong>Revenue:</strong> ${companyToDelete.totalRevenue?.toFixed(2)}</Typography>
                    )}
                    {(companyToDelete.paymentCount || 0) > 0 && (
                      <Typography variant="body2"><strong>Payments:</strong> {companyToDelete.paymentCount}</Typography>
                    )}
                  </Box>
                </Paper>

                {/* Paying customer warning — blocks deletion until acknowledged */}
                {isPayingCustomer && (
                  <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                    <Typography variant="subtitle2" gutterBottom>Paying Customer Detected</Typography>
                    <Typography variant="body2" gutterBottom>
                      This company is on a <strong>{companyToDelete.tier?.toUpperCase()}</strong> tier
                      {hasPay ? ' and has payment history on record' : ''}.
                      Deleting paying customers is restricted to prevent accidental revenue loss.
                    </Typography>
                    {(deletePayingError?.warnings || []).map((w, i) => (
                      <Typography key={i} variant="body2" sx={{ pl: 1 }}>• {w}</Typography>
                    ))}
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={deleteForceAcknowledged}
                          onChange={(e) => setDeleteForceAcknowledged(e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={600}>
                          I understand this is a paying customer and want to force-delete
                        </Typography>
                      }
                    />
                  </Alert>
                )}

                {/* Deletion scope info */}
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>This action is permanent and irreversible</Typography>
                  <Typography variant="body2">The following data will be permanently erased:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography variant="body2">BI Portal: company record, AI usage, tier history, notifications</Typography></li>
                    <li><Typography variant="body2">Website DB: users, pages, products, stores, orders, analytics, customers</Typography></li>
                    <li><Typography variant="body2">All associated contacts, newsletters, reviews, vouchers, appointments</Typography></li>
                  </Box>
                </Alert>

                {/* Name confirmation */}
                <Typography variant="body1" gutterBottom>
                  Type <strong>{companyToDelete.companyName}</strong> to confirm:
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={companyToDelete.companyName}
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  sx={{ mt: 1 }}
                  error={deleteConfirmName.length > 0 && deleteConfirmName !== companyToDelete.companyName}
                  helperText={
                    deleteConfirmName.length > 0 && deleteConfirmName !== companyToDelete.companyName
                      ? 'Name does not match'
                      : deleteConfirmName === companyToDelete.companyName
                        ? 'Name matches — ready to delete'
                        : ''
                  }
                  color={deleteConfirmName === companyToDelete.companyName ? 'success' : undefined}
                />

                {/* Backend error display */}
                {deleteCompanyMutation.isError && !deletePayingError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {deleteCompanyMutation.error?.error || deleteCompanyMutation.error?.message || 'Deletion failed. Please try again.'}
                  </Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeletePayingError(null); }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteCompany}
            disabled={
              !companyToDelete ||
              deleteConfirmName !== companyToDelete.companyName ||
              deleteCompanyMutation.isLoading ||
              ((['standard', 'professional', 'enterprise'].includes(companyToDelete?.tier) ||
                (companyToDelete?.paymentCount || 0) > 0 ||
                (companyToDelete?.totalRevenue || 0) > 0) && !deleteForceAcknowledged)
            }
          >
            {deleteCompanyMutation.isLoading
              ? 'Deleting...'
              : (['standard', 'professional', 'enterprise'].includes(companyToDelete?.tier) && deleteForceAcknowledged)
                ? 'Force Delete Paying Customer'
                : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company Limit Override Modal */}
      <Dialog open={overrideModalOpen} onClose={() => setOverrideModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {detailData?.company?.maxCompaniesOverride ? 'Modify Company Limit Override' : 'Grant Company Limit Override'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Override the tier-based company limit for the <strong>entire iCloud account</strong> that owns{' '}
            <strong>{detailData?.company?.name}</strong>. This applies to every company under that iCloud ID
            (including client companies you create on your phone before handoff).
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={overrideUnlimited}
                onChange={(e) => {
                  setOverrideUnlimited(e.target.checked);
                  if (e.target.checked) setOverrideMaxCompanies('');
                }}
                color="warning"
              />
            }
            label="Grant Unlimited Companies"
            sx={{ mb: 1 }}
          />
          {!overrideUnlimited && (
            <TextField
              autoFocus
              margin="dense"
              label="Maximum Companies"
              type="number"
              fullWidth
              value={overrideMaxCompanies}
              onChange={(e) => setOverrideMaxCompanies(e.target.value)}
              inputProps={{ min: 1, max: 100 }}
              helperText="Enter a number between 1 and 100"
              sx={{ mb: 2 }}
            />
          )}
          {overrideUnlimited && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This account will be able to create an unlimited number of companies regardless of their subscription tier.
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Reason for Override"
            multiline
            rows={3}
            fullWidth
            required
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            helperText="Required: Explain why this override is being granted"
          />
          {overrideError && (
            <Alert severity="error" sx={{ mt: 2 }}>{overrideError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideModalOpen(false)} disabled={overrideLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleGrantOverride}
            disabled={overrideLoading || (!overrideUnlimited && (!overrideMaxCompanies || parseInt(overrideMaxCompanies, 10) < 1)) || !overrideReason.trim()}
          >
            {overrideLoading ? 'Saving...' : (detailData?.company?.maxCompaniesOverride ? 'Modify Override' : 'Grant Override')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Business-Type Override Modal */}
      <Dialog open={btOverrideModalOpen} onClose={() => setBtOverrideModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {detailData?.company?.allowedBusinessTypesOverride?.length ? 'Modify Business-Type Override' : 'Grant Business-Type Override'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select the business types this account should be able to access, regardless of the
            global enabled list. Selected types will be merged with the globally enabled types
            on the iOS app.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={1}>
              {BUSINESS_TYPE_CATALOG.map((entry) => {
                const checked = btOverrideSelected.includes(entry.key);
                return (
                  <Grid item xs={12} sm={6} key={entry.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={checked}
                          onChange={() => {
                            setBtOverrideSelected(prev =>
                              prev.includes(entry.key)
                                ? prev.filter(k => k !== entry.key)
                                : [...prev, entry.key]
                            );
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{entry.label}</Typography>
                          <Typography variant="caption" color="textSecondary">{entry.key}</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          <TextField
            label="Reason"
            multiline
            rows={3}
            fullWidth
            required
            value={btOverrideReason}
            onChange={(e) => setBtOverrideReason(e.target.value)}
            helperText="Required: Explain why this override is being granted (e.g., 'Pilot test of jewelry vertical for Acme Corp')"
          />
          {btOverrideError && (
            <Alert severity="error" sx={{ mt: 2 }}>{btOverrideError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBtOverrideModalOpen(false)} disabled={btOverrideLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleGrantBusinessTypesOverride}
            disabled={btOverrideLoading || btOverrideSelected.length === 0 || !btOverrideReason.trim()}
          >
            {btOverrideLoading ? 'Saving...' : (detailData?.company?.allowedBusinessTypesOverride?.length ? 'Modify Override' : 'Grant Override')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Resolve Fraud Flag Dialog ──────────────────────────────────────── */}
      <Dialog open={resolveFraudOpen} onClose={() => setResolveFraudOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Resolve Fraud Flag
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Mark all unresolved fraud flags as resolved for{' '}
            <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong>.
            The account will no longer appear in the fraud queue.
          </Typography>
          <TextField
            autoFocus
            label="Admin Notes"
            multiline
            rows={3}
            fullWidth
            required
            value={resolveFraudNotes}
            onChange={e => setResolveFraudNotes(e.target.value)}
            placeholder="Explain why this flag is being resolved (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResolveFraudOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!resolveFraudNotes.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.resolveFraud(company.companyId, { notes: resolveFraudNotes, resolvedBy: 'admin' });
                setResolveFraudOpen(false);
                setFraudActionSuccess('Fraud flags resolved successfully');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to resolve fraud flags');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Resolving...' : 'Resolve Flags'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Change Company Tier Dialog ─────────────────────────────────────── */}
      <Dialog open={changeTierOpen} onClose={() => !changeTierMutation.isLoading && setChangeTierOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Company Tier</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Permanently set the subscription tier for <strong>{detailData?.company?.name}</strong>.
            This uses admin override and syncs to the website database.
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="change-tier-label">Tier</InputLabel>
            <Select
              labelId="change-tier-label"
              label="Tier"
              value={changeTierValue}
              onChange={(e) => setChangeTierValue(e.target.value)}
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Reason"
            multiline
            rows={3}
            fullWidth
            required
            value={changeTierReason}
            onChange={(e) => setChangeTierReason(e.target.value)}
            placeholder="Reason for this tier change (required)"
          />
          {changeTierError && <Alert severity="error" sx={{ mt: 2 }}>{changeTierError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeTierOpen(false)} disabled={changeTierMutation.isLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!changeTierReason.trim() || changeTierMutation.isLoading}
            onClick={() => {
              if (!detailData?.company?.companyId) return;
              changeTierMutation.mutate({
                companyId: detailData.company.companyId,
                tier: changeTierValue,
                reason: changeTierReason.trim(),
              });
            }}
          >
            {changeTierMutation.isLoading ? 'Saving...' : 'Change Tier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Force Downgrade Dialog ─────────────────────────────────────────── */}
      <Dialog open={forceDowngradeOpen} onClose={() => setForceDowngradeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowDownwardIcon color="warning" />
          Force Downgrade to Free Tier
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately downgrade <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong> to
            the <strong>Free</strong> tier. This action is logged in the tier history.
          </Alert>
          <TextField
            autoFocus
            label="Reason"
            multiline
            rows={3}
            fullWidth
            required
            value={forceDowngradeReason}
            onChange={e => setForceDowngradeReason(e.target.value)}
            placeholder="Enter the reason for this admin override (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setForceDowngradeOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!forceDowngradeReason.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.forceDowngrade(company.companyId, { reason: forceDowngradeReason });
                setForceDowngradeOpen(false);
                setFraudActionSuccess('Company downgraded to Free tier');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to downgrade company');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Downgrading...' : 'Force Downgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Suspend Account Dialog ────────────────────────────────────────── */}
      <Dialog open={suspendOpen} onClose={() => setSuspendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon color="error" />
          Suspend Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will immediately suspend <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong>.
            The iOS app will be unable to send heartbeats or register until the suspension is lifted.
          </Alert>
          <TextField
            autoFocus
            label="Reason for Suspension"
            multiline
            rows={3}
            fullWidth
            required
            value={suspendReason}
            onChange={e => setSuspendReason(e.target.value)}
            placeholder="Enter the reason for suspension (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuspendOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!suspendReason.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.suspendAccount(company.companyId, { reason: suspendReason });
                setSuspendOpen(false);
                setFraudActionSuccess('Account suspended');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to suspend account');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Suspending...' : 'Suspend Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Permanently Ban iCloud User Dialog ───────────────────────────── */}
      <Dialog open={banOpen} onClose={() => setBanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOffIcon color="error" />
          Permanently Ban iCloud User
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>⚠️ This action is permanent</Typography>
            This will ban the iCloud user linked to{' '}
            <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong> across ALL
            their company records (active and archived). Future registration attempts from this iCloud
            account will be rejected by the server.
          </Alert>
          <TextField
            autoFocus
            label="Reason for Ban"
            multiline
            rows={3}
            fullWidth
            required
            value={banReason}
            onChange={e => setBanReason(e.target.value)}
            placeholder="Enter the reason for the permanent ban (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBanOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!banReason.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.banUser(company.companyId, { reason: banReason });
                setBanOpen(false);
                setFraudActionSuccess('iCloud user permanently banned');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to ban user');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Banning...' : 'Permanently Ban User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unsuspendOpen} onClose={() => setUnsuspendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lift Suspension</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Restore heartbeat and registration for{' '}
            <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong>.
          </Typography>
          <TextField
            autoFocus
            label="Admin Notes"
            multiline
            rows={3}
            fullWidth
            required
            value={unsuspendNotes}
            onChange={e => setUnsuspendNotes(e.target.value)}
            placeholder="Why is this suspension being lifted? (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUnsuspendOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!unsuspendNotes.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.unsuspendAccount(company.companyId, { notes: unsuspendNotes });
                setUnsuspendOpen(false);
                setFraudActionSuccess('Account suspension lifted');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to lift suspension');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Lifting...' : 'Lift Suspension'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unbanOpen} onClose={() => setUnbanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lift iCloud Ban</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This restores registration and heartbeat for the iCloud user linked to{' '}
            <strong>{selectedCompanyDetail?.name || detailData?.company?.name}</strong>.
          </Alert>
          <TextField
            autoFocus
            label="Reason"
            multiline
            rows={3}
            fullWidth
            required
            value={unbanReason}
            onChange={e => setUnbanReason(e.target.value)}
            placeholder="Why is this ban being lifted? (required)"
          />
          {fraudActionError && <Alert severity="error" sx={{ mt: 2 }}>{fraudActionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUnbanOpen(false); setFraudActionError(null); }} disabled={fraudActionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!unbanReason.trim() || fraudActionLoading}
            onClick={async () => {
              const company = selectedCompanyDetail || detailData?.company;
              if (!company) return;
              setFraudActionLoading(true);
              setFraudActionError(null);
              try {
                await companiesAPI.unbanUser(company.companyId, { reason: unbanReason });
                setUnbanOpen(false);
                setFraudActionSuccess('iCloud ban lifted');
                refreshAfterFraudAction();
              } catch (err) {
                setFraudActionError(err.message || 'Failed to lift ban');
              } finally {
                setFraudActionLoading(false);
              }
            }}
          >
            {fraudActionLoading ? 'Lifting...' : 'Lift Ban'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies;
